/**
 * Every mark the HUD is made of, in one place.
 *
 * 🔴 **Why this is a module and not two copies.** The contact sheet
 * (`tools/design-hud-sheet.mjs`) and the in-play frames
 * (`tools/design-hud-inplay.mjs`) have to be pictures of *the same thing*. The
 * moment they are drawn by two bodies of code, the sheet stops being evidence
 * about what the screen will look like — and that is exactly the fault haugur
 * found on 2026-08-17, one level up: **a thing judged in a view it will never
 * be seen in.**
 *
 * 🔴 **Why it lives in the CLIENT and not in `tools/`.** haugur chose this
 * (option A, 2026-08-17) over the client drawing its own HUD or the two sharing
 * a copy. One module means the sample sheets are drawn by **the game's own
 * code**, so they are evidence about the real screen *by construction* rather
 * than because somebody remembered to keep two files in step. The four
 * generators in `tools/` are plain `.mjs` and import this `.ts` file directly —
 * Node 24 strips the types, and `erasableSyntaxOnly` in `tsconfig.base.json` is
 * what guarantees it can.
 *
 * ⚠️ **It emits SVG strings, so the client side is a positioned element whose
 * contents are rebuilt when a value CHANGES — never every frame.**
 *
 * Everything is parameterised by slot geometry, because that is the only thing
 * that legitimately differs between the two callers:
 *
 * | | `SLOT` | `GAP` | Ten slots |
 * |---|---|---|---|
 * | The sheet | 56 | 5 | 605 px — sized to be read on a sheet |
 * | ⚠️ **The game** | **48** | **4** | **516 px on a 1280 screen** — the real one |
 *
 * ⚠️ **The second row is the one that decides anything.** The first exists so a
 * printed comparison is legible and nothing more.
 */

/** The slot geometry a drawing is made at — the only thing the two callers differ in. */
export interface Geometry {
  readonly SLOT: number;
  readonly GAP: number;
}

/**
 * Which drawing language a mark is in.
 *
 * The first four are the directions the sheet compares; `carved` is the one
 * haugur chose (option D, the scrim) and `tally` is the stripped version kept
 * as the far end of the "how little ink" question.
 */
export type Kind = 'skorur' | 'kola' | 'studlaberg' | 'vedurmaelir' | 'carved' | 'tally';

/**
 * 👕 The four parts of the body a garment is worn on (T153).
 *
 * ⚠️ **The same four as `BodyPart` in `@lofi/shared`, agreed rather than
 * imported** — this module may not reach for the workspace, because the four
 * design generators in `tools/` resolve it as a plain file. `hud-scene.test.ts`
 * is what checks the two lists stay the same.
 */
export type Garment = 'head' | 'body' | 'legs' | 'feet';

/** Every item the HUD draws by hand. 🎨 T141 — the project's item icons. */
export type ItemKey =
  | 'kraekiber'
  | 'blaber'
  | 'birkistofn'
  | 'moberg'
  | 'vidarkol'
  | 'birkiplanki'
  | 'prik'
  | 'rekavidur'
  | 'rekaplanki'
  | 'kistill'
  | 'kyndill'
  | 'treoxi'
  | 'trehaki'
  | 'treskofla'
  | 'steinoxi'
  | 'steinhaki'
  | 'steinskofla'
  | 'spjot'
  | 'hnifur';

/**
 * ⚠️ Every optional key below is written `?: T | undefined`, not `?: T`.
 *
 * `exactOptionalPropertyTypes` is on, which makes those two different: the
 * short form forbids passing `undefined` *explicitly*, and `hotbar` does
 * exactly that when a set takes no slot numbers. The long form is what the
 * existing code already assumes.
 */

/** What `txt` understands. */
export interface TextOptions {
  readonly fill?: string | undefined;
  readonly font?: string | undefined;
  readonly size?: number | undefined;
  readonly weight?: number | undefined;
  readonly ls?: number | undefined;
  readonly anchor?: string | undefined;
  readonly style?: string | undefined;
}

/** What `struck` understands — text with no ground, carrying its own halo. */
export interface StruckOptions {
  readonly fill?: string | undefined;
  readonly font?: string | undefined;
  readonly size?: number | undefined;
  readonly weight?: number | undefined;
  readonly anchor?: string | undefined;
  /** Width of the halo stroke, in px. */
  readonly sw?: number | undefined;
}

/** What `rect` understands. */
export interface RectOptions {
  readonly rx?: number | undefined;
  readonly stroke?: string | undefined;
  /** Stroke width, in px. Numeric, because bodies multiply it. */
  readonly sw?: number | undefined;
  readonly op?: number | undefined;
}

/** What `exposure` understands. Only the needle dial uses one. */
export interface ExposureOptions {
  /**
   * Radius of the dial, in px. A parameter because the dial is the one element
   * that cannot be squeezed into a bar-height strip.
   */
  readonly r?: number | undefined;
}

/** What a slot is holding. */
export interface SlotContent {
  /**
   * The item key.
   *
   * ⚠️ **A plain string, not {@link ItemKey}.** Every breakable block is an item
   * now (29 of them), and only four have a picture drawn by hand. The rest are
   * drawn as a cube from {@link SlotContent.colour}.
   */
  readonly item?: string | undefined;
  /**
   * The block's own colour, for an item with no hand-drawn picture.
   *
   * 🔴 **Passed in rather than looked up, and that is load-bearing.** This
   * module must not import `@lofi/shared`: the four design generators in
   * `tools/` are plain `.mjs` and resolve this file directly, and a workspace
   * import from here would break every sample sheet. The client knows the
   * palette; this only knows how to draw.
   */
  readonly colour?: string | undefined;
  /**
   * A finished picture of the item, as a data URI.
   *
   * 🔴 **This is how a block item gets the block's real texture on it** — a
   * cube rendered from the tile the game actually draws, built in
   * `blockicons.ts` where a canvas exists. Preferred over {@link colour}, which
   * is the flat fallback for a block with no tile at all.
   *
   * ⚠️ **Passed in, never fetched.** This module emits SVG strings and nothing
   * else; see the note on {@link colour} for why it may not reach for the
   * palette itself.
   */
  readonly iconUri?: string | undefined;
  /**
   * 🪓 **A PHOTOGRAPH of the item itself, as a data URI.**
   *
   * 🎯 **haugur, 2026-09-02:** *"i would really like our tools in game to look
   * like that please."* The six tools are photographs now; `item-icons.json`
   * holds them with their background already knocked out.
   *
   * 🔴 **A separate field from {@link iconUri}, and merging the two would be a
   * bug.** `iconUri` means *"the BLOCK this item places, drawn as a cube"*.
   * This means *"the item itself"*. They sit at different places in the order —
   * a photograph beats the drawn icon, and the drawn icon beats the block cube —
   * so one field could not express both.
   *
   * ⚠️ **Passed in, never fetched**, for the same reason as {@link colour}: this
   * module may not import `@lofi/shared`.
   */
  readonly photoUri?: string | undefined;
  readonly count?: number | undefined;
  /**
   * The name printed under the picture.
   *
   * 🎯 **haugur, 2026-08-18:** *"all i get is a small pic of the item in the
   * slot, we need to add text under the pic to tell the player what it is."*
   *
   * ⚠️ **Absent draws nothing and leaves the slot exactly as it was**, which is
   * what keeps the approved sample sheets byte-identical.
   */
  readonly label?: string | undefined;
  /**
   * 🔨 **How worn a tool is — 0 is new, 1 is finished.** D104, T-wear.
   *
   * 🎯 **haugur chose the bar, 2026-09-09**, from four drawn at true size in
   * `Design/samples/82`: a bar under the tool, *"Minecraft's, in our
   * colours."*
   *
   * 🔴 **Absent, or 0, draws NOTHING**, and that is what keeps every approved
   * sample sheet byte-identical for everything that is not a used tool. A fresh
   * axe looks exactly as it did before this existed.
   *
   * ⚠️ **A fraction, not a count.** This module may not import `@lofi/shared`
   * (see {@link SlotContent.colour}), so it cannot know that a stone axe lasts
   * 300 blocks and a wooden one 100. **The caller divides**;
   * `slotContentFor` is the one place that does it.
   */
  readonly wear?: number | undefined;
}

/** Everything `slot` can be told, including where it sits in the row. */
export interface SlotOptions extends SlotContent {
  readonly selected?: boolean | undefined;
  /** The digit in the corner — a *string*, because the tenth slot prints "0". */
  readonly index?: string | undefined;
}

/**
 * How an item is drawn.
 *
 * 🔴 **The icon and nothing else.** This carried an English and an Icelandic
 * name until 2026-08-18, and they were never drawn by anything — dead fields
 * that had already drifted: the HUD said the English for `moberg` was *Móberg*
 * while `items.json` and `blocks.json` both say *Moberg*. **A second copy of a
 * name is a second thing to keep true**, which is the whole fault this module
 * was moved into the client to stop. Item names live in `items.json`, read
 * through `@lofi/shared`; `hud-icons.test.ts` is what keeps the two lists the
 * same length.
 */
export interface ItemDef {
  readonly icon: (x: number, y: number, s: number) => string;
}

/** A reading's icon: drawn at a size, in a colour. */
export type IconFn = (x: number, y: number, s: number, fill: string) => string;

/** What `inventory` gives back — the panel's own size travels with its drawing. */
export interface InventoryPanel {
  readonly svg: string;
  readonly w: number;
  readonly h: number;
}

export function makeParts({ SLOT, GAP }: Geometry) {
  const BARW = 10 * SLOT + 9 * GAP;

  /* ── The palette ──────────────────────────────────────────────────────────
     Every colour here is either already in packages/web/app/globals.css or is
     derived from a block colour the way §3 and §11.1 already derive one. The
     three new ones are marked. Ratios are computed, not estimated. */
  const C = {
    nott: '#0D1519', //  the ground              — globals.css
    badstofa: '#18262A', //  the one raised surface  — globals.css
    kerti: '#F2E9DA', //  ink, and HEALTH         — globals.css
    tolg: '#BFB29C', //  muted ink, labels       — globals.css
    lampi: '#D9B463', //  the accent, and FOOD    — globals.css
    rodi: '#E09A78', //  firelight on skin, HOT  — globals.css
    /* 🎯 haugur, 2026-08-17: he knows *skorur* and *askur*; he does **not** know
       *héla*, and that word was the only one of the four that could reach a
       player's screen — it labels the cold end of the exposure gauge. It is now
       `kalt`, which he approved: *"kalt is fine."* ⚠️ The colour is unchanged.
       The test for a word on this project is not whether it exists but whether
       haugur knows it. */
    kalt: '#8FCBE0', //  NEW — cold. Hue 199°, the sea-and-ice hue of §12.4
    hverleir: '#C97A5F', //  NEW — hot-spring clay, lightened. HEALTH IN DANGER
    hola: '#22333A', //  NEW — the empty part of a bar
    hairline: 'rgba(242,233,218,0.14)',
    hairfaint: 'rgba(242,233,218,0.07)',
    /* NEW, 2026-08-17 evening — the no-panel work.
       `key` is nótt taken almost to black. It is not a colour anyone sees as a
       colour; it is the DARK HALF of every doubled edge (V34), and its job is
       arithmetic: paired with kerti it cannot fall below 4.05:1 against any
       ground in existence, and paired with lampi it cannot fall below 3.17:1.
       `grof` is the inside of a cut — deeper than `hola`, because a recess that
       is the same value as a flat empty cell is a flat empty cell. */
    key: '#070B0D',
    grof: '#1A272D',
  };

  /* ── Two values from one ───────────────────────────────────────────────────
     A carved thing is legible because it has a lit face and a shadowed one. So
     every rod on screen is mixed from ONE palette colour, never picked by eye —
     the highlight and the shade are that colour toward white and toward black.
     Mixing in sRGB on purpose: these are surfaces catching light in a picture,
     not physically-correct light. */
  const hex2rgb = (h: string): number[] => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const rgb2hex = (c: number[]): string =>
    '#' + c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
  const mix = (h: string, t: number, to: number): string =>
    rgb2hex(hex2rgb(h).map((v) => v + (to - v) * t));
  const lighten = (h: string, t: number): string => mix(h, t, 255);
  const darken = (h: string, t: number): string => mix(h, t, 0);

  const SERIF = "'EB Garamond','Iowan Old Style',Palatino,Georgia,serif";
  const SANS = "'Alegreya Sans','Segoe UI',system-ui,-apple-system,sans-serif";

  const esc = (s: string | number): string =>
    String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const txt = (x: number, y: number, s: string | number, o: TextOptions = {}): string =>
    `<text x="${x}" y="${y}" fill="${o.fill ?? C.tolg}" font-family="${o.font ?? SANS}" font-size="${o.size ?? 13}" font-weight="${o.weight ?? 400}" letter-spacing="${o.ls ?? 0}" text-anchor="${o.anchor ?? 'start'}"${o.style ? ` style="${o.style}"` : ''}>${esc(s)}</text>`;

  const eyebrow = (x: number, y: number, s: string, fill: string = C.tolg): string =>
    txt(x, y, s.toUpperCase(), { size: 10.5, weight: 700, ls: 2.4, fill });

  const rect = (
    x: number,
    y: number,
    w: number,
    h: number,
    fill: string,
    o: RectOptions = {},
  ): string =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}"${o.rx ? ` rx="${o.rx}"` : ''} fill="${fill}"${o.stroke ? ` stroke="${o.stroke}" stroke-width="${o.sw ?? 1}"` : ''}${o.op ? ` opacity="${o.op}"` : ''}/>`;

  /* ══════════════════════════════════════════════════════════════════════════
     ICONS.  Every reading carries an icon AND a word AND a number — V7, and it
     is not optional here: §4's colour-blind check found the palette's warm inks
     collapse into one another. Colour is the LEAST reliable of the four signals.
     ══════════════════════════════════════════════════════════════════════════ */

  /** Health. A candle flame — Kvöldvaka's own thesis: health is the light you have left. */
  const icFlame: IconFn = (x, y, s, fill) =>
    `<g transform="translate(${x},${y}) scale(${s / 16})"><path d="M8 0.6c2.6 3 4.6 5 4.6 8.1A4.6 4.6 0 0 1 8 13.3a4.6 4.6 0 0 1-4.6-4.6C3.4 5.6 5.4 3.6 8 .6Z" fill="${fill}"/><path d="M8 5.4c1.2 1.6 2.1 2.6 2.1 4.1A2.1 2.1 0 0 1 8 11.6a2.1 2.1 0 0 1-2.1-2.1C5.9 8 6.8 7 8 5.4Z" fill="${C.nott}" opacity="0.55"/></g>`;

  /** Food. An askur — the lidded wooden bowl every Icelander ate from. Not a drumstick. */
  const icAskur: IconFn = (x, y, s, fill) =>
    `<g transform="translate(${x},${y}) scale(${s / 16})"><path d="M1.4 6.4h13.2l-1.7 6.2a2 2 0 0 1-1.9 1.4H5a2 2 0 0 1-1.9-1.4Z" fill="${fill}"/><rect x="0.6" y="4.5" width="14.8" height="1.7" rx="0.85" fill="${fill}"/><path d="M8 1.4v3" stroke="${fill}" stroke-width="1.4" stroke-linecap="round"/></g>`;

  /** Cold. Rime on a stone — barbed, crystalline, hard. NOT a Christmas snowflake. */
  const icRime: IconFn = (x, y, s, fill) =>
    `<g transform="translate(${x},${y}) scale(${s / 16})" stroke="${fill}" stroke-width="1.35" stroke-linecap="round" fill="none"><path d="M8 1.2v13.6M2.1 4.6l11.8 6.8M13.9 4.6 2.1 11.4"/><path d="M5.6 2.6 8 4.2l2.4-1.6M5.6 13.4 8 11.8l2.4 1.6"/></g>`;

  /** Heat. Three rising columns of warm air. Soft where the cold mark is barbed —
      so the two ends of the gauge differ in TEXTURE as well as in colour. */
  const icHeat: IconFn = (x, y, s, fill) =>
    `<g transform="translate(${x},${y}) scale(${s / 16})" stroke="${fill}" stroke-width="1.35" stroke-linecap="round" fill="none"><path d="M4 13.6c0-2.4 2-2.4 2-4.8s-2-2.4-2-4.8"/><path d="M8 14.4c0-2.6 2.2-2.6 2.2-5.2S8 6.6 8 4"/><path d="M12 13.6c0-2.4 2-2.4 2-4.8"/></g>`;

  /* ── Item icons.  🎨 T141 — THE FIRST ITEM ICONS THE PROJECT HAS EVER HAD.
     Rule they establish: an item is drawn as the OBJECT, flat, no outline, two
     values plus one highlight, on no ground, and its colours come from the block
     palette so a móberg lump in your hand is the móberg you dug it from. */

  const itKraekiber = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})"><path d="M16 26c-3-4-7-6-7-10" stroke="#4E5A38" stroke-width="2" fill="none" stroke-linecap="round"/><circle cx="11" cy="20" r="5.2" fill="#241E2C"/><circle cx="21" cy="18" r="5.8" fill="#2B2333"/><circle cx="16" cy="26" r="5" fill="#1E1A26"/><circle cx="9.4" cy="18.2" r="1.5" fill="#6E5F80" opacity="0.75"/><circle cx="19.2" cy="16" r="1.7" fill="#7A6A8C" opacity="0.75"/><path d="M20 9c3.5-1.5 6 0 6 0s-2 3-5.4 2.6" fill="#5F6C3E"/></g>`;

  const itBlaber = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})"><circle cx="12" cy="20" r="5.4" fill="#3D4C74"/><circle cx="21" cy="22" r="5.8" fill="#46567F"/><circle cx="17" cy="13.5" r="5" fill="#4E5F8A"/><circle cx="10.3" cy="18" r="1.5" fill="#93A6CC" opacity="0.8"/><circle cx="19.2" cy="20" r="1.6" fill="#9DAFD4" opacity="0.8"/><path d="M6 12c4-3 8-1 8-1s-3 4-7 3z" fill="#69723F"/></g>`;

  const itBirkistofn = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})"><rect x="4" y="10" width="24" height="13" rx="2.2" fill="#B0AA9D"/><rect x="4" y="18.4" width="24" height="4.6" fill="#948E82" opacity="0.55"/><ellipse cx="27" cy="16.5" rx="3.1" ry="6.5" fill="#8C8578"/><ellipse cx="27" cy="16.5" rx="1.7" ry="3.9" fill="#6E6A60"/><g fill="#2A2723"><rect x="8" y="12.6" width="4.6" height="1.5" rx="0.7"/><rect x="15" y="16" width="3.4" height="1.5" rx="0.7"/><rect x="10" y="19.6" width="5.4" height="1.5" rx="0.7"/><rect x="19" y="12.2" width="2.6" height="1.5" rx="0.7"/></g></g>`;

  const itMoberg = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})"><path d="M5 19 9 8l11-3 8 6-2 12-13 3z" fill="#887B6A"/><path d="m9 8 11-3 8 6-10 3z" fill="#9C9080"/><path d="m18 14 10-3-2 12-13 3z" fill="#6B6153"/><g fill="#5B5348" opacity="0.6"><circle cx="12" cy="15" r="1"/><circle cx="16" cy="20" r="0.9"/><circle cx="22" cy="19" r="1.1"/><circle cx="10" cy="20" r="0.8"/></g></g>`;

  /**
   * 🔥 **Charcoal — `vidarkol`.** Three broken lumps with an ash bloom on them.
   *
   * 🔴 **It had to be drawn by hand because it places NOTHING.** Charcoal is
   * fuel and trade goods, not a material, so there is no block to face a cube
   * with — and the drawability guard in `hud-icons.test.ts` refused the item the
   * moment it was added, which is the guard doing its job.
   *
   * 🔴 **It is drawn as GREY WITH DARK FACETS, not as black, and that is the
   * whole of the problem this icon has.**
   *
   * ⚠️ **A slot's ground is `nott` — #0D1519 — at half opacity over the world.**
   * The first version used #26231F and #3A352F, which is what charcoal actually
   * looks like, and **on that ground it was very nearly invisible.** ✅ **Caught
   * by drawing it at 16, 24, 32 and 48 px and looking**, which is the rule
   * *judge every icon at true size* existing for a reason.
   *
   * 👉 **The lesson generalises: the darkest thing in the game cannot be drawn
   * with its own colour on a dark HUD.** `birkistofn` has the same shape of
   * problem from the other end — real birch bark is nearly silver and it is
   * drawn nearly silver, which works only because the ground is dark.
   */
  const itVidarkol = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})"><path d="M6 21 9 13l7-2 3 6-4 6z" fill="#4A443D"/><path d="m9 13 7-2 3 6-8 1z" fill="#635B51"/><path d="m18 24 3-8 6-1 1 7-5 3z" fill="#443E38"/><path d="m21 16 6-1 1 7-6-1z" fill="#5C554C"/><path d="m13 26 2-5 5 1-1 5z" fill="#332F2A"/><g fill="#A79E93" opacity="0.7"><circle cx="12" cy="15" r="1.3"/><circle cx="24" cy="18" r="1.1"/><circle cx="16" cy="23.5" r="0.9"/></g></g>`;

  /* 🛠️ ── The wood ladder, 2026-08-31 ─────────────────────────────────────
     🔴 ALL THREE ARE HAND-DRAWN BECAUSE NONE OF THEM PLACES A BLOCK. The
     drawability guard in `hud-icons.test.ts` refused all three the moment they
     were added, which is that guard doing exactly the job charcoal taught it.

     ⚠️ Their colours are one family on purpose — a plank, a stick cut from
     that plank and a torch made from that stick are the SAME birch, and if the
     three drifted apart the ladder would stop reading as a ladder. Keyed off
     `birkistofn`'s #B0AA9D, warmed the way sawn wood is warmer than bark. */

  /**
   * 🪵 **Birch planks — `birkiplanki`.** Two boards, the front one lying
   * across the back one, drawn in the same isometric as {@link itCube} so it
   * sits in a row of block items without looking like a different drawing.
   *
   * ⚠️ **Two boards rather than one**, because one board at 16 px is a stick.
   * The step down to the second board is what says *timber*, and it is the only
   * thing distinguishing this from {@link itPrik} at a glance.
   */
  const itBirkiplanki = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})">` +
    `<path d="M6 12.5 22 4.5l5 2.5-16 8z" fill="#B3A68D"/>` +
    `<path d="M6 12.5 11 15v3.4l-5-2.5z" fill="#8A7E69"/>` +
    `<path d="M11 15 27 7v3.4l-16 8z" fill="#9C9077"/>` +
    `<path d="M3 19.5 19 11.5l5 2.5-16 8z" fill="#CBBEA4"/>` +
    `<path d="M3 19.5 8 22v4.2l-5-2.5z" fill="#8F836E"/>` +
    `<path d="M8 22 24 14v4.2l-16 8z" fill="#A99C83"/>` +
    `<g fill="#A4977E" opacity="0.55">` +
    `<path d="M6.6 18.6 20 11.9l.9.5-13.4 6.7z"/>` +
    `<path d="M9.4 20 22.8 13.3l.9.5-13.4 6.7z"/>` +
    `</g>` +
    `<path d="M3 19.5 19 11.5l5 2.5" fill="none" stroke="#E6DBC4" stroke-width="0.9" stroke-linejoin="round"/>` +
    `</g>`;

  /**
   * 🪵 **Driftwood planks — `rekaplanki`.**
   *
   * 🔴 **The same two boards as {@link itBirkiplanki}, in the sea’s colours.**
   * Deliberately the same drawing: they are the same THING, and D102 makes the
   * pack a place where you choose in a hurry — two different plank shapes would
   * teach the eye to read shape when what actually differs is **colour**.
   *
   * ⚠️ **Grey where birch is warm**, keyed off the block’s own #A2968A against
   * `birkiplanki`’s #BE9E6E. That is the whole difference and it has to survive
   * 16 px, where the boards themselves are barely two strokes.
   *
   * ⚠️ **And it needed a second thing at 16 px.** Colour alone is what V7/V28
   * forbids as a sole signal, so the top board carries **two split lines** the
   * birch plank does not — sea-weathered timber checks along the grain, and it
   * reads as texture rather than as a different object.
   */
  const itRekaplanki = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})">` +
    `<path d="M6 12.5 22 4.5l5 2.5-16 8z" fill="#A2968A"/>` +
    `<path d="M6 12.5 11 15v3.4l-5-2.5z" fill="#736B62"/>` +
    `<path d="M11 15 27 7v3.4l-16 8z" fill="#867E74"/>` +
    `<path d="M3 19.5 19 11.5l5 2.5-16 8z" fill="#B8ADA1"/>` +
    `<path d="M3 19.5 8 22v4.2l-5-2.5z" fill="#7A7268"/>` +
    `<path d="M8 22 24 14v4.2l-16 8z" fill="#948B80"/>` +
    /* The splits — the second signal, so the difference is not colour alone. */
    `<g fill="#6E665D" opacity="0.7">` +
    `<path d="M7.4 18.2 20.8 11.5l.7.35-13.4 6.7z"/>` +
    `<path d="M10.6 19.8 24 13.1l.7.35-13.4 6.7z"/>` +
    `</g>` +
    `<path d="M3 19.5 19 11.5l5 2.5" fill="none" stroke="#D2C8BC" stroke-width="0.9" stroke-linejoin="round"/>` +
    `</g>`;

  /**
   * 📦 **A chest — `kistill`.** The cube, with a lid line and a lock plate.
   *
   * 🔴 **Drawn in the same isometric as {@link itCube}**, because it IS a cube
   * — the pack should show the thing you are about to place, not a picture of a
   * different object that happens to mean the same.
   *
   * ⚠️ **The lock plate is the only mark that survives 16 px**, so it is bigger
   * than a real one would be, and centred on the front face where a lock is.
   * Everything else — the bands, the boards — is at 32 px and above.
   *
   * ⚠️ **In the block’s own #7A6242 and two values off it**, which is well below
   * both planks. That gap is deliberate: a chest standing against a plank wall
   * must read as an OBJECT rather than as more wall, and it is the same reason
   * the texture was chosen dark.
   */
  const itKistill = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})">` +
    /* The three faces. */
    `<path d="M16 4 28 10.5 16 17 4 10.5z" fill="#8E7350"/>` +
    `<path d="M4 10.5 16 17v11L4 21.5z" fill="#5F4C34"/>` +
    `<path d="M16 17 28 10.5v11L16 28z" fill="#7A6242"/>` +
    /* The lid, cut across all three so it reads as a lid and not as a shadow. */
    `<path d="M4 14.2 16 20.7 28 14.2" fill="none" stroke="#4A3B29" stroke-width="1.1" stroke-linejoin="round"/>` +
    /* The iron bands, one down each visible face. */
    `<g fill="#3B342C" opacity="0.85">` +
    `<path d="M7.6 12.45 9.4 13.4v11l-1.8-1v-11z"/>` +
    `<path d="M22.6 13.4 24.4 12.45v11l-1.8 1v-11z"/>` +
    `</g>` +
    /* 🔴 The lock plate — the one mark that has to survive 16 px. */
    `<path d="M14.4 19.6 17.6 19.6v4.1l-3.2 1.9z" fill="#2E2820"/>` +
    `<path d="M15.4 21.1 16.6 21.1v1.5l-1.2.7z" fill="#C9B27A"/>` +
    `</g>`;

  /**
   * 🪵 **A stick — `prik`.** One tapered shaft on the diagonal with a
   * single side twig.
   *
   * ⚠️ **On the diagonal, and that is the tile rule arriving in the HUD.**
   * `09_Texture_Generation.md` found it for the workbench's motifs: at small
   * sizes a shape laid flat across the middle wastes the square, and the
   * diagonal uses all of it. A horizontal stick at 16 px is a dash.
   */
  /**
   * 🪵 **Driftwood — rekaviður.** TWO crossed pieces, not one stick.
   *
   * 🔴 **Crossed, because that is what tells it apart from prik at 32 px.**
   * A single diagonal shaft is already the stick, and D102 makes the pack a
   * place where you choose between similar things in a hurry. Two pieces
   * lying across each other is also what a heap on the beach actually looks
   * like — `driftgeom.ts` builds 2–5 of them per cell for the same reason.
   *
   * ⚠️ **In the block’s own colours, #706A62 and two values off it**, the way
   * every item icon takes its colours from the block palette. It is a colder,
   * greyer wood than prik’s warm #A5937A, and that difference is the whole
   * point: birch is warm, driftwood is grey-brown after years in the sea.
   */
  const itRekavidur = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})">` +
    /* The piece underneath, running low-left to high-right. */
    `<path d="M4.6 24.2 26.8 12.6l1.5 2.9L6.1 27.1z" fill="#5E574F"/>` +
    `<path d="M4.6 24.2 26.8 12.6l.7 1.4L5.3 25.6z" fill="#8A8177"/>` +
    /* The piece on top, across it, a little thicker and a little paler. */
    `<path d="M7.2 8.4 24.4 26.2l-2.4 2.1L4.9 10.5z" fill="#706A62"/>` +
    `<path d="M7.2 8.4 24.4 26.2l-1.1 1L6.1 9.4z" fill="#9A9086"/>` +
    `<path d="M9.6 11.2 21.6 24.6l-.8.7L8.8 12z" fill="#4F4A44" opacity="0.55"/>` +
    /* A split running along the top piece, which is what says WEATHERED. */
    `<circle cx="15.6" cy="17.4" r="0.9" fill="#4A453F" opacity="0.75"/>` +
    `</g>`;

  const itPrik = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})">` +
    `<path d="M6.4 26.4 22.6 6.9l2.6 2.2-16.2 19.5z" fill="#A5937A"/>` +
    `<path d="M6.4 26.4 22.6 6.9l1.3 1.1L7.7 27.5z" fill="#C6B69C"/>` +
    `<path d="M8 27.6 24.2 8.1l1 .9L9 28.6z" fill="#7E6F5D"/>` +
    `<path d="M15.6 16.4 21.4 13l.8 1.6-5.8 3.3z" fill="#9A8971"/>` +
    `<circle cx="13.4" cy="19.6" r="0.9" fill="#7A6B59" opacity="0.8"/>` +
    `</g>`;

  /**
   * 🔦 **A torch — `kyndill`.** The stick on the same diagonal as
   * {@link itPrik}, a charred head, and a flame standing upright out of it.
   *
   * 🔴 **The flame is drawn in the FLAME's own colours**, straight off the
   * ramp in `tools/textures/flame.mjs` — #BA3A0E, #E8701A, #F8B642 and a
   * #FFF4CD core. ⚠️ **Not the project palette, and deliberately:** doc 15
   * records that a flame is not a flag and gets fire colours. **The torch in
   * your hand and the fire in the world must be the same fire.**
   *
   * ⚠️ **The flame stands upright rather than following the stick.** Fire
   * goes up whatever it is burning on, and a flame leaning with the shaft reads
   * as a lit match falling over.
   */
  const itKyndill = (x: number, y: number, s: number): string =>
    `<g transform="translate(${x},${y}) scale(${s / 32})">` +
    `<path d="M7.6 26.2 16.6 11.8l2.8 1.6-9 14.4z" fill="#A5937A"/>` +
    `<path d="M7.6 26.2 16.6 11.8l1.4.8-9 14.4z" fill="#C6B69C"/>` +
    `<path d="M15.3 12.6 19.2 15l2.4-3.8-3.9-2.4z" fill="#4A443D"/>` +
    `<path d="M15.3 12.6 17.2 13.8l2.4-3.8-1.9-1.2z" fill="#635B51"/>` +
    `<path d="M16.3 10.2c0-3.2 2.7-4.2 3.3-7.6 2 2.8 3.4 4.4 3.4 7.6 0 2.4-1.6 4-3.35 4S16.3 12.6 16.3 10.2z" fill="#BA3A0E"/>` +
    `<path d="M17.6 10.6c0-2.2 1.7-3 2.1-5.4 1.4 2 2.2 3.2 2.2 5.4 0 1.6-1 2.7-2.15 2.7S17.6 12.2 17.6 10.6z" fill="#E8701A"/>` +
    `<path d="M18.6 10.9c0-1.3.9-1.9 1.15-3.3.85 1.2 1.25 2 1.25 3.3 0 .9-.6 1.6-1.2 1.6s-1.2-.7-1.2-1.6z" fill="#F8B642"/>` +
    `<ellipse cx="19.8" cy="11.2" rx="0.62" ry="0.95" fill="#FFF4CD"/>` +
    `</g>`;

  /* ══════════════════════════════════════════════════════════════════════════
     🪓⛏️🥄 THE SIX TOOLS — T130, D102/D103/D104
     ══════════════════════════════════════════════════════════════════════════ */

  /**
   * 🪓⛏️🥄 **One tool icon, built from a head shape and a head colour.**
   *
   * 🔴 **One function and six calls, not six drawings.** Six near-identical
   * blobs is how a set stops looking like a set — one of them gets a nudge
   * nobody repeats, and a year later the stone axe hangs differently from the
   * wooden one. It is the same instinct as the ITEMS note above: **one list,
   * never two kept in step.**
   *
   * ⚠️ **The handle is the `prik` shaft, unchanged** — same diagonal, same three
   * colours. That is not laziness: a tool IS two prik and a head (every one of
   * the six recipes says so), so the picture should say it too.
   *
   * 🎨 **Two values from one colour**, the rule {@link itCube} already follows.
   * The head is never two colours chosen side by side; it is one material
   * catching the light, so wood and stone differ by a single hex value.
   */
  const itTool = (head: string, shape: (c: (t: number) => string) => string) => {
    const c = (t: number): string => (t > 0 ? lighten(head, t) : darken(head, -t));
    return (x: number, y: number, s: number): string =>
      `<g transform="translate(${x},${y}) scale(${s / 32})">` +
      /* 🪵 The shaft first, so the head sits over it — and it runs the WHOLE
         diagonal and pokes out past the head at the top right, which is what
         Minecraft's sprites do and what makes a tool read as a head fixed to a
         stick rather than a stick with a lump on the end. */
      `<path d="M5.0 27.6 24.4 6.2 27.0 8.6 7.6 30.0z" fill="${SHAFT}"/>` +
      `<path d="M5.0 27.6 24.4 6.2 25.7 7.4 6.3 28.8z" fill="${SHAFT_LIT}"/>` +
      `<path d="M6.3 28.8 25.7 7.4 27.0 8.6 7.6 30.0z" fill="${SHAFT_DARK}"/>` +
      shape(c) +
      `</g>`;
  };

  /**
   * 🪓 **An axe.** A lopsided crescent blade — **wider than it is tall**, with a
   * notch bitten out of the underside where the blade meets the haft.
   *
   * 🔴 **The proportion is the whole design, and it was found by looking.** The
   * first version was a wedge of roughly the same mass as the shovel, and on
   * `Design/samples/48` at 16 px **the two were the same picture**: a blob on a
   * stick. ⚠️ **That is not a cosmetic fault** — D102 says the tool must match
   * the block, so a player who cannot tell an axe from a shovel in the slot is
   * playing a guessing game at the exact moment the rule bites.
   *
   * 👉 **So the axe is now WIDE and the shovel is NARROW**, and the notch gives
   * the axe an asymmetric outline the symmetric shovel cannot have.
   */
  const axeHead = (c: (t: number) => string): string =>
    `<path d="M21.6 8.0 13.2 2.8Q5.6 6.4 8.0 14.6L18.4 15.8Z" fill="${c(0)}"/>` +
    `<path d="M21.6 8.0 13.2 2.8Q7.4 5.6 7.6 10.6L20.4 10.9Z" fill="${c(0.34)}"/>` +
    `<path d="M13.2 2.8Q7.4 5.6 7.6 10.6L6.6 10.4Q6.6 4.8 12.8 1.6Z" fill="${c(0.7)}"/>`;

  /**
   * ⛏️ **A pick.** A long arched bar, high in the middle and **dropping to a
   * point at each end** — the silhouette that cannot be mistaken for an axe even
   * at sixteen pixels, which is the size that decides whether an icon works.
   *
   * 🔴 **The arch used to be the other way up, and that was simply wrong.** With
   * the middle low and the tips raised, the shaft ran up into the dip and the
   * whole thing read as a **wishbone**, not a pick. **A pickaxe's points aim at
   * the ground**; that is what the shape is for, and inverting the curve is the
   * entire difference between a tool and a catapult.
   */
  const pickHead = (c: (t: number) => string): string =>
    `<path d="M4.6 14.2Q17.2 2.2 28.6 15.0L25.6 17.4Q17.0 7.8 7.4 16.2Z" fill="${c(0)}"/>` +
    `<path d="M4.6 14.2Q17.2 2.2 28.6 15.0L27.4 16.0Q17.1 4.8 5.9 15.2Z" fill="${c(0.4)}"/>` +
    `<path d="M7.4 16.2Q17.0 7.8 25.6 17.4L24.6 18.2Q17.0 9.4 8.4 17.0Z" fill="${c(-0.28)}"/>`;

  /**
   * 🥄 **A shovel.** A blade narrow where it meets the haft and **widening to a
   * flat digging edge** at the far end, taller than it is wide.
   *
   * 🔴 **It used to taper to a POINT and it read as a spearhead.** Found by
   * looking at `Design/samples/48` big rather than by any test — the shape was
   * distinct from the axe, which is what the sheet was built to check, and still
   * wrong about what a shovel is. **A shovel gets wider away from your hands;
   * that is the whole of what makes it a shovel.**
   *
   * ⚠️ **Still the opposite proportion to the axe, on purpose** — see
   * {@link axeHead} for why that is load-bearing rather than a preference.
   */
  const shovelHead = (c: (t: number) => string): string =>
    `<path d="M15.4 13.2 20.8 3.4Q24.6 1.8 27.0 5.0L28.4 9.4 18.8 16.0Z" fill="${c(0)}"/>` +
    `<path d="M15.4 13.2 20.8 3.4Q24.6 1.8 27.0 5.0L22.0 8.4Z" fill="${c(0.34)}"/>` +
    `<path d="M20.8 3.4Q24.6 1.8 27.0 5.0L25.9 5.7Q24.2 3.2 21.3 4.4Z" fill="${c(0.7)}"/>`;

  /**
   * 🎨 **The two head materials.**
   *
   * 🔴 **Both taken from blocks that already exist**, so a tool cannot drift
   * away from what it is made of: the wooden head is `birkiplanki`'s birch and
   * the stone head is `moberg`'s grey. ⚠️ **Móberg rather than grágrýti** — a
   * stone tool is made from any of the three rocks (the `grjot` tag), and móberg
   * is the one a player will have first.
   */
  const HEAD_WOOD = '#E2C68F';
  const HEAD_STONE = '#8E8C87';

  /**
   * 🪵 **The shaft is ALWAYS wood, and always DARKER than either head.**
   *
   * 🎯 **haugur, 2026-09-01:** *"always a wood handle, but a wood colored top for
   * wood tools and a stone colored top for stone tools."*
   *
   * 🔴 **It was already always wood — what was wrong is that you could not SEE
   * it.** The shaft was `prik`'s #A5937A and the wooden head `birkiplanki`'s
   * #BE9E6E, near enough the same tan that a wooden tool read as one lump while
   * the stone ones read correctly.
   *
   * ✅ **Minecraft solves it two ways and we can only use one of them.** Reading
   * the real sprites: its stick is a genuinely DARK brown (#493615 → #896727)
   * against a pale head, *and* every head carries a hard dark outline. **T141
   * says our icons have no outlines**, so all the work has to be done by value —
   * hence a darker shaft and a paler birch head than either was before.
   */
  const SHAFT = '#8A7659';
  const SHAFT_LIT = '#A28C6B';
  const SHAFT_DARK = '#5F503B';

  const itTreoxi = itTool(HEAD_WOOD, axeHead);
  const itTrehaki = itTool(HEAD_WOOD, pickHead);
  const itTreskofla = itTool(HEAD_WOOD, shovelHead);
  const itSteinoxi = itTool(HEAD_STONE, axeHead);
  const itSteinhaki = itTool(HEAD_STONE, pickHead);
  const itSteinskofla = itTool(HEAD_STONE, shovelHead);

  /**
   * 🔱 **A spear.** A long narrow point that continues the shaft's own line.
   *
   * 🔴 **THE SILHOUETTE IS THE DESIGN, AND IT IS THE ONE THING A SPEAR GETS FOR
   * FREE.** The axe and the shovel both had to be pulled apart by hand — wide
   * against narrow, with a notch — **because both sit ACROSS the shaft and at
   * 16 px two heads on a stick are the same picture.** ✅ **A spearhead points
   * the way the shaft already goes**, so it cannot be confused with either of
   * them however small it is drawn. **That is not luck; it is what a spear is.**
   *
   * ⚠️ **It is the THINNEST icon in the game and that is the risk.** `viðarkol`
   * looked right at 1024 px and was nearly invisible at 48. 🎯 **haugur judged
   * this one at 48 px and at 34 px before it was drawn** —
   * `Design/samples/106_spjot_candidates.png` — and chose it seeing that.
   *
   * 📐 Three values from one colour, no outline, exactly as T141 requires: the
   * body, the lit upper face, and one highlight along the leading edge.
   */
  const spearHead = (c: (t: number) => string): string =>
    `<path d="M30.5 4.5 22.9 9.6 21.0 15.0 25.7 13.0Z" fill="${c(0)}"/>` +
    `<path d="M30.5 4.5 22.9 9.6 21.0 15.0Z" fill="${c(0.34)}"/>` +
    `<path d="M30.5 4.5 22.9 9.6 24.6 8.7Z" fill="${c(0.7)}"/>`;

  const itSpjot = itTool(HEAD_STONE, spearHead);

  /**
   * 🔪 **A knife — D125.** A SHORT handle and a LONG blade, the reverse of every
   * other tool here.
   *
   * 🔴 **It cannot use {@link itTool}**, because that draws the shaft down the
   * whole diagonal — and a long stick with a blade at the top of it is a SPEAR.
   * **What separates a knife from a spear at 16 px is where the wood stops**, so
   * the handle ends a third of the way up and the stone takes the rest.
   *
   * ⚠️ **This is the shape your HAND holds** — `helditem.ts` turns these paths
   * into triangles. The PACK shows haugur's photograph (*"knife 1"*).
   *
   * 📐 Three values from one colour and no outline (T141): the blade, its lit
   * spine, and a highlight near the point. **The spine is straight and the edge
   * bellies out**, which is what makes it read as a cutting blade and not a
   * spearhead, whose two sides are the same.
   */
  const itHnifur = (x: number, y: number, s: number): string => {
    const c = (t: number): string => (t > 0 ? lighten(HEAD_STONE, t) : darken(HEAD_STONE, -t));
    return (
      `<g transform="translate(${x},${y}) scale(${s / 32})">` +
      `<path d="M5.0 27.6 12.4 19.4 15.0 21.8 7.6 30.0z" fill="${SHAFT}"/>` +
      `<path d="M5.0 27.6 12.4 19.4 13.7 20.6 6.3 28.8z" fill="${SHAFT_LIT}"/>` +
      `<path d="M6.3 28.8 13.7 20.6 15.0 21.8 7.6 30.0z" fill="${SHAFT_DARK}"/>` +
      `<path d="M11.4 18.4 29.5 2.5Q25.2 15.2 13.8 20.8Z" fill="${c(0)}"/>` +
      `<path d="M11.4 18.4 29.5 2.5 12.8 19.8Z" fill="${c(0.34)}"/>` +
      `<path d="M22.0 8.8 29.5 2.5 23.0 9.8Z" fill="${c(0.7)}"/>` +
      /* 🪢 The leather cord where stone meets wood — his photograph has it. */
      `<path d="M10.6 19.2 12.2 17.6 15.4 20.6 13.8 22.2z" fill="${SHAFT_DARK}"/>` +
      `</g>`
    );
  };

  /**
   * A block item, drawn as the block.
   *
   * 🔴 **From the block's OWN colour, so it cannot drift from the block it
   * represents.** Every block in `blocks.json` carries a researched colour;
   * this lightens it for the top face and darkens it for the right, which is
   * the same "two values from one" rule the rods use — one material catching
   * light, never two colours chosen side by side.
   *
   * Minecraft draws block items as little cubes for the same reason: **the
   * picture of a thing you can place should be the thing.** The four items with
   * a hand-drawn picture keep it — an object beats a cube where we have one.
   */
  const itCube = (x: number, y: number, s: number, colour: string): string => {
    const u = s / 32;
    const top = lighten(colour, 0.3);
    const left = colour;
    const right = darken(colour, 0.34);
    /* An isometric cube on a 32-unit grid: 16 across the half-width, 8 for the
       face rise. Drawn as three quadrilaterals with no outline (T141's rule). */
    const P = (px: number, py: number): string => `${x + px * u},${y + py * u}`;
    return (
      `<g>` +
      `<path d="M${P(16, 4)} L${P(29, 11)} L${P(16, 18)} L${P(3, 11)} z" fill="${top}"/>` +
      `<path d="M${P(3, 11)} L${P(16, 18)} L${P(16, 29)} L${P(3, 22)} z" fill="${left}"/>` +
      `<path d="M${P(29, 11)} L${P(16, 18)} L${P(16, 29)} L${P(29, 22)} z" fill="${right}"/>` +
      /* One highlight along the lit arris, so the top edge reads as an edge. */
      `<path d="M${P(3, 11)} L${P(16, 4)} L${P(29, 11)}" fill="none" stroke="${lighten(colour, 0.6)}" stroke-width="${u}" stroke-linejoin="round"/>` +
      `</g>`
    );
  };

  /**
   * 🔴 **There used to be a second list here, and charcoal fell down the gap
   * between the two** (found 2026-08-31, drawing the hearth screen for T128).
   *
   * A `HAND_DRAWN` set named the four items whose drawing should beat a cube,
   * and {@link slot} asked *that* set before reaching into {@link ITEMS}.
   * `vidarkol` was added to `ITEMS` with a carefully drawn icon — three broken
   * lumps, redrawn once because the first attempt was invisible on this ground —
   * **and never added to the set. So it drew nothing at all.** Charcoal appeared
   * as a bare number in every slot that held it.
   *
   * ⚠️ **Nothing failed, and the guard that exists could not have caught it:**
   * `hud-icons.test.ts` checks that every item has *a way* to be drawn, and it
   * reads `ITEMS` — **the list that was right.** A test pointed at the
   * neighbouring list of the one the drawing actually reads.
   *
   * ✅ **So the second list is gone rather than corrected.** `ITEMS` is now the
   * only answer to *is there a picture for this*, and there is no longer a pair
   * that can disagree. 🔴 **This is the `pack.ts` / `preview-textures.ts` trap
   * in `06_Traps.md`** — a hand-edited list whose omissions produce no error and
   * no picture — and the fix for that trap is one list, never two kept in step.
   */
  const ITEMS: Record<ItemKey, ItemDef> = {
    kraekiber: { icon: itKraekiber },
    blaber: { icon: itBlaber },
    birkistofn: { icon: itBirkistofn },
    moberg: { icon: itMoberg },
    vidarkol: { icon: itVidarkol },
    birkiplanki: { icon: itBirkiplanki },
    rekavidur: { icon: itRekavidur },
    rekaplanki: { icon: itRekaplanki },
    kistill: { icon: itKistill },
    prik: { icon: itPrik },
    kyndill: { icon: itKyndill },
    treoxi: { icon: itTreoxi },
    trehaki: { icon: itTrehaki },
    treskofla: { icon: itTreskofla },
    steinoxi: { icon: itSteinoxi },
    steinhaki: { icon: itSteinhaki },
    steinskofla: { icon: itSteinskofla },
    spjot: { icon: itSpjot },
    hnifur: { icon: itHnifur },
  };

  /* ══════════════════════════════════════════════════════════════════════════
     THE FOUR DIRECTIONS
     ══════════════════════════════════════════════════════════════════════════ */

  /** A resource bar (health / food) at `pct`, in the style of set `k`. */
  function bar(
    k: Kind,
    x: number,
    y: number,
    w: number,
    pct: number,
    colour: string,
    h = 14,
  ): string {
    const g = [];
    const filled = Math.max(0, Math.min(1, pct / 100));

    if (k === 'skorur') {
      /* Ten notches on the hotbar's own grid — one per ten percent, because the
         game's arithmetic IS decimal: health is 0–100 and one berry restores 10.
         You can count how many berries you still need. */
      const cw = (w - 9 * GAP) / 10;
      for (let i = 0; i < 10; i++) {
        const cx = x + i * (cw + GAP);
        const on = filled * 10 - i;
        g.push(rect(cx, y, cw, h, C.hola));
        /* The ghost top edge keeps the TOTAL countable when the bar is nearly
           empty — the single thing most bars get wrong. */
        g.push(rect(cx, y, cw, 1.5, C.tolg, { op: 0.22 }));
        if (on > 0) g.push(rect(cx, y, cw * Math.min(1, on), h, colour));
      }
    } else if (k === 'kola') {
      /* One continuous level, like oil in a kola. The head of the bar carries a
         brighter 2 px meniscus, so the EDGE — the only thing you actually read —
         is the brightest pixel on the row. */
      g.push(rect(x, y, w, h, C.hola, { rx: 2 }));
      if (filled > 0) {
        g.push(rect(x, y, w * filled, h, colour, { rx: 2 }));
        g.push(rect(x + w * filled - 2.5, y, 2.5, h, C.kerti, { op: 0.85 }));
        g.push(rect(x, y, w * filled, 2, C.kerti, { op: 0.22 }));
      }
      for (let i = 1; i < 10; i++)
        g.push(rect(x + (w / 10) * i, y + h - 4, 1, 4, C.nott, { op: 0.5 }));
    } else if (k === 'studlaberg') {
      /* Ten columns, but quarried rather than drawn: the joints are irregular and
         the tops are uneven, so it reads as basalt and not as a progress bar.
         Still exactly ten, so it is still countable. */
      /* eslint-disable security/detect-object-injection --
         Every index here is a loop counter against a literal array of fixed
         length. Nothing a player types reaches this module at all. */
      const jog = [0, 4, -3, 2, -2, 5, -4, 1, 3, -2, 0];
      const edges: number[] = [];
      for (let i = 0; i <= 10; i++) edges.push(x + (w / 10) * i + jog[i]!);
      const tops = [0, 1.5, -1, 2, 0.5, -1.5, 1, -0.5, 2, 0];
      for (let i = 0; i < 10; i++) {
        const cx = edges[i]! + 1,
          cw = edges[i + 1]! - edges[i]! - 2;
        const ty = y + tops[i]!;
        /* eslint-enable security/detect-object-injection */

        const on = filled * 10 - i;
        g.push(rect(cx, ty, cw, h, C.hola));
        g.push(rect(cx, ty, cw, 1.5, C.tolg, { op: 0.2 }));
        if (on > 0) {
          g.push(rect(cx, ty, cw * Math.min(1, on), h, colour));
          g.push(rect(cx, ty, Math.min(cw, cw * Math.min(1, on)), 2, C.kerti, { op: 0.18 }));
        }
      }
    } else {
      /* vedurmaelir — the instrument. A hairline rule with a filled keel and a
         tick every ten. The quietest of the four: it looks like a scale, not a
         tank, which is what an instrument should look like. */
      g.push(rect(x, y + h / 2 - 1, w, 2, C.hola));
      g.push(rect(x, y + h / 2 - 1, w * filled, 2, colour));
      for (let i = 0; i <= 10; i++) {
        const tx = x + (w / 10) * i;
        const past = i / 10 <= filled;
        g.push(
          rect(tx, y + (i % 5 === 0 ? 0 : 3), 1, i % 5 === 0 ? h : h - 6, past ? colour : C.hola),
        );
      }
      /* The head is a bead ON the rule, not a flag above it — a marker that
         overhangs its own row collides with the label of the row above. */
      if (filled > 0)
        g.push(`<circle cx="${x + w * filled}" cy="${y + h / 2}" r="4.5" fill="${colour}"/>`);
    }
    return g.join('');
  }

  /**
   * The exposure gauge. `v` is −1 (hard frost) … 0 (comfortable) … +1 (too hot).
   *
   * 🔴 It is deliberately NOT the same shape as the two bars above it, in every
   * one of the four sets. Health and food DEPLETE; exposure DEVIATES. Drawn as a
   * third depleting bar, a player reads "half empty" as "bad" and the comfortable
   * middle becomes unreadable.
   */
  function exposure(
    k: Kind,
    x: number,
    y: number,
    w: number,
    v: number,
    h = 14,
    o: ExposureOptions = {},
  ): string {
    const g = [];
    const mid = x + w / 2;
    const half = w / 2;
    const cold = v < 0;
    const col = cold ? C.kalt : C.rodi;
    const mag = Math.abs(v);

    if (k === 'skorur') {
      const cells = 11;
      const cw = (w - (cells - 1) * 4) / cells;
      for (let i = 0; i < cells; i++) {
        const cx = x + i * (cw + 4);
        const d = i - 5; // −5 … +5
        const lit = d !== 0 && Math.sign(d) === Math.sign(v) && Math.abs(d) <= Math.round(mag * 5);
        g.push(rect(cx, y, cw, h, d === 0 ? C.badstofa : C.hola));
        if (d === 0) {
          /* The comfortable middle is never blank — a gauge that shows nothing
             when nothing is wrong is indistinguishable from a gauge that is off. */
          g.push(rect(cx, y, cw, h, C.tolg, { op: mag < 0.02 ? 0.75 : 0.3 }));
          g.push(rect(cx + cw / 2 - 0.75, y - 5, 1.5, h + 10, C.tolg, { op: 0.7 }));
        }
        if (lit) g.push(rect(cx, y, cw, h, col));
      }
    } else if (k === 'kola') {
      g.push(rect(x, y, w, h, C.hola, { rx: 2 }));
      g.push(rect(mid - 6, y, 12, h, C.tolg, { op: mag < 0.02 ? 0.7 : 0.28 }));
      g.push(rect(mid - 1, y - 5, 2, h + 10, C.tolg, { op: 0.7 }));
      if (mag > 0.02) {
        const len = half * mag;
        g.push(rect(cold ? mid - len : mid, y, len, h, col, { rx: 2 }));
        g.push(rect(cold ? mid - len : mid + len - 2.5, y, 2.5, h, C.kerti, { op: 0.85 }));
      }
    } else if (k === 'studlaberg') {
      /* The seam in a lava edge: it opens from the middle. The COLD side grows
         crystalline vertical ticks, the HOT side a molten saw edge — so the two
         directions differ in texture and not only in hue (V7). */
      g.push(rect(x, y, w, h, C.hola));
      g.push(rect(mid - 7, y, 14, h, C.tolg, { op: mag < 0.02 ? 0.7 : 0.28 }));
      g.push(rect(mid - 1.5, y - 4, 3, h + 8, C.tolg, { op: 0.75 }));
      if (mag > 0.02) {
        const len = half * mag;
        const bx = cold ? mid - len : mid;
        g.push(rect(bx, y, len, h, col));
        if (cold) {
          for (let t = bx + 3; t < mid - 2; t += 7)
            g.push(rect(t, y, 1.4, h, C.nott, { op: 0.35 }));
        } else {
          const pts = [];
          for (let t = 0; t <= len; t += 6) pts.push(`${bx + t},${y + (t % 12 === 0 ? 0 : 4)}`);
          g.push(
            `<polyline points="${pts.join(' ')}" fill="none" stroke="${C.nott}" stroke-width="1.6" opacity="0.4"/>`,
          );
        }
      }
    } else {
      /* The honest home for a bipolar reading: a needle on a scale, with a
         comfortable band drawn in. Veðurstofa Íslands is literally the data
         source, so an instrument face is not a costume. */
      /* ⚠️ The radius is a parameter because the dial is the one element that
         cannot be squeezed into a bar-height strip. At true size the first
         in-play frame had it hanging out of the panel and over the on-hand
         row — **which is a real cost of this option, not a drawing slip.** */
      const r = o.r ?? 44;
      const cy = y + h + r * 0.6;
      const a = (deg: number): number => (deg * Math.PI) / 180;
      const arc = (a0: number, a1: number, stroke: string, sw: number, op = 1): string => {
        const x0 = mid + r * Math.cos(a(a0)),
          y0 = cy + r * Math.sin(a(a0));
        const x1 = mid + r * Math.cos(a(a1)),
          y1 = cy + r * Math.sin(a(a1));
        return `<path d="M${x0.toFixed(1)} ${y0.toFixed(1)} A${r} ${r} 0 0 1 ${x1.toFixed(1)} ${y1.toFixed(1)}" fill="none" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`;
      };
      g.push(arc(180, 360, C.hola, 7));
      g.push(arc(180, 232, C.kalt, 7, 0.4));
      g.push(arc(308, 360, C.rodi, 7, 0.4));
      g.push(arc(252, 288, C.tolg, 7, 0.55));
      const ang = 270 + v * 90;
      const nx = mid + (r - 13) * Math.cos(a(ang)),
        ny = cy + (r - 13) * Math.sin(a(ang));
      g.push(
        `<line x1="${mid}" y1="${cy}" x2="${nx.toFixed(1)}" y2="${ny.toFixed(1)}" stroke="${mag < 0.2 ? C.tolg : col}" stroke-width="2.6" stroke-linecap="round"/>`,
      );
      g.push(`<circle cx="${mid}" cy="${cy}" r="3.4" fill="${mag < 0.2 ? C.tolg : col}"/>`);
    }
    return g.join('');
  }

  /* ══════════════════════════════════════════════════════════════════════════
     SKORUR, CARVED.  The second pass, 2026-08-17 evening.

     🎯 **haugur:** *"i like nr 1 best but the bars remind me of a excel
     document, not something that has been designed for a in game splendor.
     maybe we should also try ti without the background for the entire things,
     its bulky"*

     🔴 **He is describing a drawing fault, not a layout fault.** A skorur is a
     stick somebody cut notches into with a knife. The first pass drew it as ten
     flat rectangles with a number at the end — which is a table, and a table is
     what he saw. So this pass draws the OBJECT: one continuous rod, notched at
     every tenth, with a lit top arris and a shadowed underside, and the spent
     part carved away into a channel.

     🔴 **And the depth is not decoration — it is the whole answer to killing
     the panel.** See V34 in the guide: no single value survives this screen
     (the ground under the bars runs from L 0.003 to L 0.795 *in one row*), but
     a pale body against a near-black keyline cannot fall below 4.05:1 against
     any ground that exists. **The panel was solving with area what an edge
     solves with two pixels.**
     ══════════════════════════════════════════════════════════════════════════ */

  /** The dark half of every doubled edge, plus the contact shadow, in one shape. */
  const keyed = (x: number, y: number, w: number, h: number): string =>
    rect(x - 1, y - 1, w + 2, h + 3, C.key);

  /**
   * A resource rod. `pct` 0–100, `col` is the material (kerti = bone, lampi = horn).
   *
   * Cross-section at h = 11, top to bottom: 1 px lit arris · 1 px highlight ·
   * 6 px body · 2 px shade · 1 px deep shade. That is a *cylinder*, and it is
   * why the thing reads as a rod lying on the screen rather than as a fill.
   */
  function carvedBar(x: number, y: number, w: number, pct: number, col: string, h = 11): string {
    const g = [];
    const f = Math.max(0, Math.min(1, pct / 100));
    const wf = Math.round(w * f);

    /* 🔴 **The spent length is SHALLOWER, not merely darker.** This is the
       second thing that made the first pass read as a table: an empty cell that
       is the same size and shape as a full one is a cell, and ten of them are a
       row of a spreadsheet. Here the stick has been *cut down* — the spent part
       is a 6 px groove at the bottom of an 11 px band, with 4 px of open world
       above it. **The bar's WEIGHT is the reading**, not only its colour. And a
       bar that loses mass as it empties also loses bulk, which is the other
       half of what haugur was complaining about. */
    const CH = 6;
    const cy = y + h - CH;
    g.push(keyed(x, cy, w, CH));
    g.push(rect(x, cy, w, CH, C.grof));
    g.push(rect(x, cy, w, 2, '#0F181D'));
    g.push(rect(x, y + h - 1, w, 1, C.kerti, { op: 0.1 }));

    /* 🔴 The ghost lip — where the surface used to be. It runs the FULL length
       whether or not there is material under it, so the TOTAL is always
       described: the single thing most bars get wrong is that an almost-empty
       one stops saying how big it was.

       🔴 **Both values are SOLID, and that is the night fix (V36).** They were
       key at 72 % and tólg at 42 %, and daylight hid what that costs: an
       opacity is not a value, it is a value *plus whatever is behind it*, so a
       faded pair is not a doubled edge at all — it is two tints of the ground.
       Measured on the rendered frame at 48 % health, this lip carried the whole
       spent length at **2.38:1 at night** (the groove floor beside it managed
       1.20:1), under the 3:1 floor V34 sets. Solid, the pair is tólg L 0.4681
       against key L 0.0031, and the same closed form V34 uses puts the worst any
       ground can do at **3.12:1** — over the floor, and true of every background
       that can exist.

       ⚠️ **This cannot change a full bar in either frame.** The fill is drawn
       over the lip a few lines below, so the lip is only ever visible along the
       spent length. At 100 % nothing here is on screen at all. */
    g.push(rect(x, y - 1, w, 2, C.key));
    g.push(rect(x, y, w, 1, C.tolg));

    if (wf > 0) {
      g.push(keyed(x, y, wf, h));
      g.push(rect(x, y, wf, h, col));
      g.push(rect(x, y, wf, 2, lighten(col, 0.42)));
      g.push(rect(x, y, wf, 1, lighten(col, 0.74)));
      g.push(rect(x, y + h - 3, wf, 3, darken(col, 0.4)));
      g.push(rect(x, y + h - 1, wf, 1, darken(col, 0.6)));
      /* Grain. Sparse, faint, irregular — a piece of bone is not a swatch, and
         ⚠️ at 11 px this is the most that can be put in before it turns to
         mush. It is not readable as grain; it is readable as *not plastic*. */
      for (const t of [0.07, 0.19, 0.26, 0.41, 0.53, 0.61, 0.74, 0.83, 0.91])
        if (t * w < wf)
          g.push(rect(Math.round(x + t * w), y + 2, 1, h - 4, darken(col, 0.3), { op: 0.45 }));
      /* The cut end. It faces away from the light, so it is the darkest face on
         the rod — and the 1 px of key beyond it is the depth of the cut. */
      if (wf > 3) g.push(rect(x + wf - 3, y, 3, h, darken(col, 0.5)));
      if (wf < w) g.push(rect(x + wf, y, 1, h - CH, C.key, { op: 0.85 }));
    }

    /* 🔴 The notches — the whole point, and the first pass drew them too small
       to see at 11 px. Nine knife cuts, one every tenth. **10 px across and 6 px
       into the rod**, which leaves 5 px of material at the cut: a real bite, not
       a serration. That rhythm every 51.6 px is what stops the row reading as a
       table. Each is drawn twice — a dark wedge and a lit facet on the side the
       light reaches. ⚠️ Past the cut end the same notch is struck into the lip
       of the groove instead, half the size, so **the total stays countable**:
       you can still see how many notches you would need to fill it. */
    for (let i = 1; i < 10; i++) {
      const cx = Math.round(x + (w * i) / 10);
      const solid = (w * i) / 10 < wf - 2;
      const [top, dep, half] = solid ? [y, 6, 5] : [cy, 4, 3];
      const d = `M${cx - half} ${top} L${cx} ${top + dep} L${cx + half} ${top} z`;
      g.push(`<path d="${d}" fill="${C.key}" opacity="0.92"/>`);
      g.push(
        `<path d="M${cx} ${top + dep} L${cx + half} ${top}" fill="none" stroke="${solid ? lighten(col, 0.55) : C.tolg}" stroke-width="1.3" opacity="${solid ? 0.85 : 0.5}"/>`,
      );
    }
    return g.join('');
  }

  /**
   * The exposure rail, carved. `v` is −1 … 0 … +1.
   *
   * ⚠️ §13.10 warned that at rest this gauge can read as *nothing there*, and
   * named the fix: **a permanent centre keel, not more colour.** It is here — a
   * pale post standing proud of the rail at top and bottom, always, in every
   * state. A gauge that is calm and a gauge that is off now look different.
   */
  function carvedExposure(x: number, y: number, w: number, v: number, h = 11): string {
    const g = [];
    const mid = Math.round(x + w / 2);
    const half = w / 2;
    const cold = v < 0;
    const col = cold ? C.kalt : C.rodi;
    const mag = Math.abs(v);

    /* The rail is the same 6 px groove the spent part of a rod is — so the
       instrument and the resources are visibly the same workshop, and the
       exposure row is the LIGHTEST thing in the cluster when nothing is wrong,
       which is exactly what V29 asks for. */
    const CH = 6;
    const cy = y + h - CH;
    g.push(keyed(x, cy, w, CH));
    g.push(rect(x, cy, w, CH, C.grof));
    g.push(rect(x, cy, w, 2, '#0F181D'));
    g.push(rect(x, y + h - 1, w, 1, C.kerti, { op: 0.1 }));

    if (mag > 0.02) {
      /* The deviation grows out of the keel as a rod of full height: something
         has come up out of the groove, and it is not a bar filling. */
      const len = Math.round(half * mag);
      const bx = cold ? mid - len : mid;
      g.push(keyed(bx, y, len, h));
      g.push(rect(bx, y, len, h, col));
      g.push(rect(bx, y, len, 2, lighten(col, 0.42)));
      g.push(rect(bx, y, len, 1, lighten(col, 0.7)));
      g.push(rect(bx, y + h - 3, len, 3, darken(col, 0.4)));
      g.push(rect(cold ? bx : bx + len - 3, y, 3, h, darken(col, 0.5)));
    }

    /* Eleven divisions — five of cold, the comfortable one, five of heat. Struck
       into the lip of the groove in the same language as the rods, at half the
       depth, because this is a scale and not a tally: you read *how far from the
       middle*, never *how many*. ⚠️ Drawn AFTER the fill, or the fill buries them. */
    for (let i = 1; i < 11; i++) {
      const tx = Math.round(x + (w * i) / 11);
      const over = Math.abs(tx - mid) < Math.round(half * mag) + 2;
      const top = over ? y : cy;
      const d = `M${tx - 3} ${top} L${tx} ${top + 4} L${tx + 3} ${top} z`;
      g.push(`<path d="${d}" fill="${C.key}" opacity="0.9"/>`);
      g.push(
        `<path d="M${tx} ${top + 4} L${tx + 3} ${top}" fill="none" stroke="${over ? lighten(col, 0.6) : C.tolg}" stroke-width="1.2" opacity="0.6"/>`,
      );
    }

    /* The keel. Two values, standing proud — 4 px above the rail and 4 below. */
    g.push(rect(mid - 3, y - 5, 6, h + 10, C.key));
    g.push(rect(mid - 2, y - 4, 4, h + 8, mag < 0.02 ? C.tolg : darken(C.tolg, 0.42)));
    g.push(rect(mid - 2, y - 4, 4, 1, C.kerti, { op: 0.8 }));
    return g.join('');
  }

  /**
   * ⚠️ The stripped version: **no rod at all.** Ten struck marks standing on a
   * hairline, and nothing else. The least ink any of these can be made of, and
   * therefore the test of how far "no background" can be pushed before the
   * reading stops arriving in peripheral vision.
   */
  function tallyBar(x: number, y: number, w: number, pct: number, col: string, h = 11): string {
    const g = [];
    const f = Math.max(0, Math.min(1, pct / 100));
    const cw = (w - 9 * GAP) / 10;

    g.push(rect(x - 1, y + h, w + 2, 2, C.key));
    g.push(rect(x, y + h, w, 1, C.tolg, { op: 0.4 }));

    for (let i = 0; i < 10; i++) {
      const cx = Math.round(x + i * (cw + GAP) + cw / 2);
      const on = Math.max(0, Math.min(1, f * 10 - i));
      if (on > 0) {
        const mh = Math.max(3, Math.round(h * on));
        const my = y + h - mh;
        g.push(rect(cx - 4, my - 1, 8, mh + 1, C.key));
        g.push(rect(cx - 3, my, 6, mh, col));
        g.push(rect(cx - 3, my, 1, mh, lighten(col, 0.55)));
        g.push(rect(cx + 2, my, 1, mh, darken(col, 0.45)));
        g.push(rect(cx - 3, my, 6, 1, lighten(col, 0.7)));
      } else {
        /* A scar where the mark would be — never nothing, or the total stops
           being countable the moment the bar runs low. */
        g.push(rect(cx - 2, y + 2, 4, h - 2, C.key, { op: 0.7 }));
        g.push(rect(cx - 1, y + 2, 1, h - 2, C.tolg, { op: 0.24 }));
      }
    }
    return g.join('');
  }

  /** The exposure reading in the same stripped language: marks either side of a keel. */
  function tallyExposure(x: number, y: number, w: number, v: number, h = 11): string {
    const g = [];
    const mid = Math.round(x + w / 2);
    const cold = v < 0;
    const col = cold ? C.kalt : C.rodi;
    const mag = Math.abs(v);
    const step = w / 11;

    g.push(rect(x - 1, y + h, w + 2, 2, C.key));
    g.push(rect(x, y + h, w, 1, C.tolg, { op: 0.34 }));

    for (let d = -5; d <= 5; d++) {
      if (d === 0) continue;
      const cx = Math.round(mid + d * step);
      const lit = Math.sign(d) === Math.sign(v) && Math.abs(d) <= Math.round(mag * 5);
      if (lit) {
        g.push(rect(cx - 4, y - 1, 8, h + 1, C.key));
        g.push(rect(cx - 3, y, 6, h, col));
        g.push(rect(cx - 3, y, 6, 1, lighten(col, 0.7)));
        g.push(rect(cx + 2, y, 1, h, darken(col, 0.45)));
      } else {
        g.push(rect(cx - 2, y + 3, 4, h - 3, C.key, { op: 0.66 }));
        g.push(rect(cx - 1, y + 3, 1, h - 3, C.tolg, { op: 0.2 }));
      }
    }
    g.push(rect(mid - 3, y - 5, 6, h + 10, C.key));
    g.push(rect(mid - 2, y - 4, 4, h + 8, mag < 0.02 ? C.tolg : darken(C.tolg, 0.42)));
    g.push(rect(mid - 2, y - 4, 4, 1, C.kerti, { op: 0.8 }));
    return g.join('');
  }

  /**
   * A reading's icon with no panel under it. **Drawn twice** — a key-coloured
   * halo behind the light body — which is V31 lifted off the block face and
   * applied to the HUD. A single-value icon over this frame disappears against
   * either a birch trunk or a shadow, and both are in the same 600 px row.
   */
  const haloed = (fn: IconFn, x: number, y: number, s: number, fill: string): string => {
    const g = [];
    for (const [dx, dy] of [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
      [1, 1],
      [-1, 1],
      [1, -1],
      [-1, -1],
    ] as [number, number][])
      g.push(`<g opacity="0.9">${fn(x + dx, y + dy, s, C.key)}</g>`);
    g.push(fn(x, y, s, fill));
    return g.join('');
  };

  /** Text with no ground: the stack-count treatment, which §13.10 proved holds. */
  const struck = (x: number, y: number, s: string | number, o: StruckOptions = {}): string =>
    `<text x="${x}" y="${y}" fill="${o.fill ?? C.tolg}" font-family="${o.font ?? SANS}" font-size="${o.size ?? 11}" font-weight="${o.weight ?? 400}" text-anchor="${o.anchor ?? 'start'}" style="paint-order:stroke;stroke:${C.key};stroke-width:${o.sw ?? 3}px;stroke-linejoin:round">${esc(s)}</text>`;

  /** One slot of the on-hand bar. */
  /**
   * 🖼️ **The picture of one item, and nothing else** — no slot, no count, no
   * name. `art` is the width and height in the same units as `x` and `y`.
   *
   * 🔴 **Extracted from {@link slot} so that the thing in your HAND and the thing
   * in your PACK are the same drawing.** 🎯 haugur, 2026-09-02, on the boxes the
   * hand used to build for itself: *"pics of shovel and axe and pick and blocks
   * in hand, still looks funky."* **The something better already existed** — it
   * was in the pack the whole time.
   *
   * ⚠️ **Four branches in a fixed order, and the order is load-bearing.**
   * `ITEMS` is asked before the BLOCK and nothing may come between them —
   * consulting a block list first is what left charcoal with no picture for as
   * long as it existed (see the note on `ITEMS`).
   *
   * 🆕 🪓 **A PHOTOGRAPH now goes in front of all three, and `drawnOnly` is why
   * that is safe.** 🎯 **haugur, 2026-09-02:** *"i would really like our tools in
   * game to look like that please."*
   *
   * 🔴 **`drawnOnly` EXISTS FOR THE PLAYER'S HAND AND FOR NOTHING ELSE, AND IT
   * IS NOT OPTIONAL POLISH.** `helditem.ts` calls this function and runs
   * **`SVGLoader` over whatever comes back**. Hand it an `<image>` and the loader
   * finds no paths, `heldItemObject` returns `null`, and **the hand falls back to
   * a bare sleeve** — the tool silently disappears out of your grip. ⚠️ Putting a
   * texture in the hand instead is the change that **turned dev and staging
   * completely black on 2026-09-01**, so the hand keeps the drawing until that is
   * done deliberately and on its own.
   */
  function picture(
    o: SlotContent,
    x: number,
    y: number,
    art: number,
    opts: { readonly drawnOnly?: boolean } = {},
  ): string {
    if (o.item === undefined) return '';
    /* 🪓 The photograph, where there is one. ⚠️ `image-rendering` is left alone
       here on purpose: this is a photograph scaled down, not a 64 px tile, and
       `pixelated` would make it crunchy rather than crisp. */
    if (opts.drawnOnly !== true && o.photoUri !== undefined) {
      return `<image href="${o.photoUri}" x="${x}" y="${y}" width="${art}" height="${art}"/>`;
    }
    const drawn = Object.hasOwn(ITEMS, o.item) ? ITEMS[o.item as ItemKey].icon : undefined;
    /* An object beats a cube where somebody drew one — berries, a log, a tool. */
    if (drawn) return drawn(x, y, art);
    /* The block, faced with its own texture. ⚠️ `image-rendering: pixelated`
       for the same reason the world uses `NearestFilter`: a 64 px tile blurred
       down to 27 loses the crispness that made it worth generating. */
    if (o.iconUri !== undefined) {
      return `<image href="${o.iconUri}" x="${x}" y="${y}" width="${art}" height="${art}" style="image-rendering:pixelated"/>`;
    }
    if (o.colour !== undefined) return itCube(x, y, art, o.colour);
    return '';
  }

  function slot(k: Kind, x: number, y: number, o: SlotOptions = {}): string {
    const g = [];
    const sel = o.selected;
    const s = SLOT;
    if (k === 'carved' || k === 'tally') {
      /* The same slot with no panel behind it. ⚠️ The old `hairline` edge —
         kerti at 14 % — is worth nothing over a sunlit birch trunk, and there is
         one crossing this row. So the slot gets the doubled edge too: a
         key-coloured keyline that doubles as its contact shadow, and a lit top
         arris inside it. **Two values, again, and for the same reason.** */
      g.push(keyed(x, y, s, s));
      g.push(rect(x, y, s, s, sel ? C.badstofa : C.grof));
      g.push(rect(x, y, s, 1, C.kerti, { op: sel ? 0.5 : 0.18 }));
      g.push(rect(x, y + s - 1, s, 1, C.key, { op: 0.8 }));
      if (sel) {
        g.push(rect(x, y, s, s, 'none', { stroke: C.kerti, sw: 1.5 }));
        g.push(rect(x, y + s - 3, s, 3, C.lampi));
      }
    } else if (k === 'skorur') {
      g.push(rect(x, y, s, s, sel ? C.badstofa : C.hola));
      g.push(rect(x, y, s, s, 'none', { stroke: sel ? C.kerti : C.hairline, sw: sel ? 1.5 : 1 }));
      if (sel) g.push(rect(x, y + s - 3, s, 3, C.lampi));
    } else if (k === 'kola') {
      g.push(rect(x, y, s, s, sel ? C.badstofa : C.hola, { rx: 4 }));
      if (sel) {
        g.push(rect(x, y, s, s, 'none', { rx: 4, stroke: C.lampi, sw: 2 }));
        g.push(
          `<rect x="${x + 1}" y="${y + 1}" width="${s - 2}" height="${s - 2}" rx="3" fill="url(#warmglow)"/>`,
        );
      } else g.push(rect(x, y, s, s, 'none', { rx: 4, stroke: C.hairline }));
    } else if (k === 'studlaberg') {
      /* A basalt column: fluted, and the selected one RISES out of the row. */
      const lift = sel ? 5 : 0;
      g.push(rect(x, y - lift, s, s + lift, sel ? C.badstofa : C.hola));
      g.push(
        rect(x, y - lift, s, s + lift, 'none', {
          stroke: sel ? C.kerti : C.hairline,
          sw: sel ? 1.5 : 1,
        }),
      );
      g.push(rect(x + 12, y - lift, 1, s + lift, C.kerti, { op: 0.05 }));
      g.push(rect(x + s - 13, y - lift, 1, s + lift, C.nott, { op: 0.3 }));
      if (sel) g.push(rect(x, y - lift, s, 3, C.lampi));
    } else {
      /* No boxes at all — cells divided by hairlines on one strip. The selected
         cell is the only one with a ground. */
      /* ⚠️ Was 0.5, and at true size over bright grass the cells vanished —
         the game's own faded control text read straight through them and you
         could not tell where one slot ended. A slot still has no BOX in this
         set; it does have a ground. */
      g.push(rect(x, y, s, s, sel ? C.badstofa : C.hola, { op: sel ? 1 : 0.9 }));
      g.push(rect(x + s, y + 8, 1, s - 16, C.hairfaint));
      if (sel) {
        g.push(rect(x, y + s - 2, s, 2, C.lampi));
        g.push(rect(x, y, s, 2, C.lampi, { op: 0.35 }));
      }
    }

    /* 🔴 With a label the picture moves up and shrinks to make room. Without
       one nothing changes, which is what keeps the approved sheets identical. */
    const art = o.label === undefined ? 34 : 27;
    const ay = o.label === undefined ? y + (s - 34) / 2 - 1 : y + 3;
    g.push(picture(o, x + (s - art) / 2, ay, art));

    /* The name, under the picture. ⚠️ **Truncated to what the slot can hold** —
       at 48 px roughly nine characters fit at 8.5 px, and a name running under
       its neighbour is worse than one that is clipped. */
    if (o.label !== undefined) {
      const room = Math.floor((s - 3) / 4.35);
      const shown = o.label.length > room ? o.label.slice(0, Math.max(1, room - 1)) + '…' : o.label;
      g.push(
        `<text x="${x + s / 2}" y="${y + s - 4}" fill="${C.tolg}" font-family="${SANS}" font-size="8.5" text-anchor="middle" style="paint-order:stroke;stroke:${C.key};stroke-width:2.5px;stroke-linejoin:round">${esc(shown)}</text>`,
      );
    }
    if (o.index !== undefined)
      g.push(txt(x + 5, y + 13, o.index, { size: 10, fill: C.tolg, style: 'opacity:.45' }));
    if (o.count! > 1) {
      /* 🔴 Over item art the background is unknown, so V14 applies: `kerti` is
         the only ink permitted, and it carries its own shadow.

         ⚠️ **With a label the count moves to the TOP right.** Both used to sit
         along the bottom edge and they collided — the name was struck through by
         its own quantity, which looked like a rendering fault rather than a
         layout one. The bottom row belongs to the name; the top belongs to the
         slot number on the left and the count on the right. */
      const cy = o.label === undefined ? y + s - 5 : y + 13;
      g.push(
        `<text x="${x + s - 5}" y="${cy}" fill="${C.kerti}" font-family="${SANS}" font-size="${o.label === undefined ? 14 : 12}" font-weight="700" text-anchor="end" style="paint-order:stroke;stroke:${C.nott};stroke-width:3.5px;stroke-linejoin:round">${o.count}</text>`,
      );
    }
    g.push(wearBar(x, y, o.wear));
    return g.join('');
  }

  /**
   * 🔨 **How worn a tool is, as a bar along the bottom of its slot.**
   *
   * 🎯 **haugur chose this from four, 2026-09-09** — `Design/samples/82`, drawn
   * at true size and at three times: *"A — a bar under it."*
   *
   * 🔴 **The wear was REAL AND INVISIBLE for eight days.** Every tool has
   * counted the blocks it broke since D104, an axe lasts 100 and a stone one
   * 300, and **the only way to learn how close yours was to the end was for it
   * to vanish out of your hand.**
   *
   * ## The three decisions in here
   *
   * ⚠️ **Nothing at all for a new tool.** Not an empty bar, not a full one —
   * **nothing**, so the row a player sees on their first day is unchanged and
   * every approved sample sheet stays byte-identical except where a tool is
   * actually worn.
   *
   * 🔴 **It empties from the RIGHT and the colour goes with it**, from `lampi`
   * to `hverleir`. ⚠️ **Not green to red**: there is no green in this HUD, and
   * those two are already what the food and health bars use for *fine* and *in
   * trouble* — **a third vocabulary for the same idea is how a screen stops
   * being readable at a glance.**
   *
   * ⚠️ **It sits ON the bottom edge, under the count and under the name.** The
   * bottom row of a slot already belongs to the name; a 3 px bar below the text
   * baseline is the one strip nothing else uses. **At 48 px it is 40 × 3.**
   *
   * ⬜ **The thing in your HAND does not show it.** `helditem.ts` calls
   * {@link picture} rather than `slot`, so it never sees this — and that is
   * right: the wear belongs to the slot, and a bar floating in 3D beside your
   * fist is a different question nobody has asked.
   */
  function wearBar(x: number, y: number, wear: number | undefined): string {
    if (wear === undefined || wear <= 0) return '';
    const left = Math.min(1, Math.max(0, 1 - wear));
    const w = SLOT - 8;
    const bx = x + 4;
    const by = y + SLOT - 6;
    /* Gold while there is plenty, hot-spring clay when it is nearly gone.
       ⚠️ Mixed between two PALETTE colours rather than picked by eye, the same
       rule every other rod on this screen follows. */
    const to = hex2rgb(C.lampi);
    const warm = rgb2hex(
      /* eslint-disable-next-line security/detect-object-injection -- `i` is
         `map`'s own counter over the three channels `hex2rgb` just returned. */
      hex2rgb(C.hverleir).map((v, i) => Math.round(v + ((to[i] ?? v) - v) * left)),
    );
    return (
      rect(bx, by, w, 3, C.key, { op: 0.85 }) +
      rect(bx, by, Math.max(1, Math.round(w * left)), 3, warm)
    );
  }

  function hotbar(
    k: Kind,
    x: number,
    y: number,
    contents: readonly SlotContent[],
    selected: number,
  ): string {
    const g = [];
    if (k === 'vedurmaelir') {
      g.push(rect(x - 10, y - 8, BARW + 20, SLOT + 16, C.nott, { rx: 4, op: 0.55 }));
      g.push(rect(x - 10, y - 8, BARW + 20, SLOT + 16, 'none', { rx: 4, stroke: C.hairfaint }));
    }
    for (let i = 0; i < 10; i++) {
      // eslint-disable-next-line security/detect-object-injection -- `i` is the loop counter, 0-9.
      const c = contents[i] ?? {};
      g.push(
        slot(k, x + i * (SLOT + GAP), y, {
          ...c,
          selected: i === selected,
          index: ['skorur', 'vedurmaelir', 'carved', 'tally'].includes(k)
            ? String((i + 1) % 10)
            : undefined,
        }),
      );
    }
    return g.join('');
  }

  /* ══════════════════════════════════════════════════════════════════════════
     👕 THE GHOST OF A GARMENT — what an empty clothing slot says (T153).

     🔴 **This is a deliberate exception to a rule haugur already approved**, and
     it is the only one. `Design/00_Visual_Language.md` §13.6: *"An empty slot is
     the trough and nothing else — no icon, no placeholder, no dashed outline. So
     an empty pack looks empty rather than furnished."*

     ⚠️ **That rule is right for the twenty-four carried slots and wrong for
     these four.** A carried slot is interchangeable: any of them holds anything,
     so a mark would furnish an empty pack for no information. **A clothing slot
     is not interchangeable** — the hat slot takes only a hat — and four
     identical empty troughs cannot say which is which. 🎯 **haugur asked for
     Minecraft's arrangement on 2026-08-19 and confirmed the marks the same day.**

     **A ghost is the object with the values taken out** (§13.7 draws an item as
     the object, flat, two values plus one highlight). One stroke, `kerti` at
     22 %: enough to name the slot, never enough to read as something worn.
     ══════════════════════════════════════════════════════════════════════════ */
  function ghost(kind: Garment, x: number, y: number): string {
    const OPACITY = 0.22;
    const size = Math.round(SLOT * 0.7);
    const gx = x + (SLOT - size) / 2;
    const gy = y + (SLOT - size) / 2;
    const line = (d: string, sw = 1.6): string =>
      `<path d="${d}" fill="none" stroke="${C.kerti}" stroke-opacity="${String(OPACITY)}" ` +
      `stroke-width="${String(sw)}" stroke-linejoin="round" stroke-linecap="round"/>`;

    if (kind === 'head') {
      /* A knitted cap: a TALL crown over a deep turned-up brim.
         ⚠️ It was a shallow dome until the first true-size look, and at 34 px a
         shallow dome and a shoe are the same low mound — the two marks were
         only told apart by the words under them, which is a mark not doing its
         job (V33). Height is what separates them. */
      return (
        line(
          `M${String(gx + 5)} ${String(gy + 21)} C${String(gx + 5)} ${String(gy + 2)} ` +
            `${String(gx + size - 5)} ${String(gy + 2)} ${String(gx + size - 5)} ${String(gy + 21)}`,
        ) +
        line(
          `M${String(gx + 2)} ${String(gy + 21)} h${String(size - 4)} v7 h${String(-(size - 4))} z`,
        )
      );
    }
    if (kind === 'body') {
      /* A sweater: body, two sleeves, and the neck that says which way up. */
      return line(
        `M${String(gx + 11)} ${String(gy + 5)} h12 l9 5 -4 8 -5 -2 v14 h-12 v-14 l-5 2 -4 -8 z`,
      );
    }
    if (kind === 'legs') {
      /* Trousers: a waist and two legs, with the gap between them doing the
         work — it is the only part that cannot be mistaken for a sweater. */
      return line(
        `M${String(gx + 8)} ${String(gy + 5)} h18 v9 l-3 16 h-6 l-1 -13 -1 13 h-6 l-3 -16 z`,
      );
    }
    /* A shoe from the SIDE — a flat sole, a heel standing up at the back and a
       toe running out to the front. ⚠️ Drawn from the side because from above a
       shoe is an oval, and an oval is not a shoe; drawn ASYMMETRIC because that
       is what distinguishes it from the cap at 34 px. */
    return (
      line(
        `M${String(gx + 3)} ${String(gy + 25)} h${String(size - 6)} v4 h${String(-(size - 6))} z`,
      ) +
      line(
        `M${String(gx + 6)} ${String(gy + 25)} V${String(gy + 14)} ` +
          `Q${String(gx + 6)} ${String(gy + 9)} ${String(gx + 12)} ${String(gy + 10)} ` +
          `L${String(gx + 15)} ${String(gy + 17)} L${String(gx + size - 4)} ${String(gy + 21)} ` +
          `V${String(gy + 25)}`,
      )
    );
  }

  /* ══════════════════════════════════════════════════════════════════════════
     🗑️ THROWING SOMETHING AWAY — the mark you put a stack on (T154).

     🎯 **haugur, 2026-08-19:** *"ok 1 is fine for now"* — destroyed, rather than
     dropped on the ground or put back into the world.

     ⚠️ **Not a dustbin.** A wheeled bin in a settler's Iceland is an anachronism
     of exactly the kind `Development/Minecraft/12_What_Not_To_Copy.md` exists to
     catch. This is **a hand opening downward**, letting something fall — the
     gesture rather than the receptacle, which needs no object that did not exist
     in 874.

     🔴 **Drawn in `rodi`, the warm ink**, and it is the only warm mark in the
     panel. Nothing else here can destroy anything, so nothing else should look
     like it can.
     ══════════════════════════════════════════════════════════════════════════ */
  function discardMark(x: number, y: number, armed: boolean): string {
    const g: string[] = [];
    const col = C.rodi;
    const op = armed ? 0.95 : 0.45;
    const line = (d: string, sw = 1.6): string =>
      `<path d="${d}" fill="none" stroke="${col}" stroke-opacity="${String(op)}" ` +
      `stroke-width="${String(sw)}" stroke-linecap="round" stroke-linejoin="round"/>`;

    /* Its own trough, so it reads as a place you can put something. */
    g.push(rect(x, y, SLOT, SLOT, C.nott, { op: 0.5, rx: 2 }));
    g.push(rect(x, y, SLOT, SLOT, 'none', { stroke: col, sw: armed ? 1.6 : 1, op }));

    /* 🔴 **The thing itself, falling away.** ⚠️ The first attempt was an open
       palm with fingers — an arc over three strokes — and at 34 px it read as a
       jellyfish. **Small marks want fewer strokes, not more detail** (§13.7:
       flat, no outline, two values and one highlight), so this is two elements:
       a lump, and the way out. */
    const cx = x + SLOT / 2;
    const cy = y + SLOT / 2;
    /* The lump — the same three-quarter cube every block item is drawn as. */
    g.push(
      line(
        `M${String(cx - 7)} ${String(cy - 13)} l7 -4 7 4 v8 l-7 4 -7 -4 z` +
          ` M${String(cx - 7)} ${String(cy - 13)} l7 4 7 -4 M${String(cx)} ${String(cy - 9)} v8`,
        1.4,
      ),
    );
    /* And the way out, under it. */
    g.push(line(`M${String(cx)} ${String(cy + 4)} v10 m-5 -5 5 5 5 -5`, 1.8));
    return g.join('');
  }

  /* ── The pack chip.  haugur: "when that is full everything you pick up goes to
        regular inventory."  If the game silently routes a pickup somewhere you
        cannot see, the player thinks it was lost. */
  function packChip(k: Kind, x: number, y: number): string {
    const w = 168,
      h = 34;
    const g = [];
    g.push(rect(x, y, w, h, C.badstofa, { rx: k === 'kola' ? 5 : 0, stroke: C.hairline }));
    g.push(
      `<g transform="translate(${x + 11},${y + 8})"><path d="M2 6h14v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z" fill="none" stroke="${C.tolg}" stroke-width="1.5"/><path d="M6 6V4a3 3 0 0 1 6 0v2" fill="none" stroke="${C.tolg}" stroke-width="1.5"/></g>`,
    );
    g.push(
      `<path d="M${x + 40} ${y + 17} h13 m-4 -4 4 4 -4 4" fill="none" stroke="${C.lampi}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>`,
    );
    g.push(itKraekiber(x + 60, y + 6, 22));
    g.push(txt(x + 88, y + 22, 'To the pack  +3', { size: 13, fill: C.kerti }));
    return g.join('');
  }

  /* ══════════════════════════════════════════════════════════════════════════
     A BLOCK BEING BROKEN.
     🔴 The one rule all four obey: EVERY MARK IS DRAWN TWICE — a light stroke and
     a dark stroke, one pixel apart. The face underneath can be jökull (#DFE7EC)
     or a downward face lit only by the HemisphereLight's ground colour
     (0x3a342b), so a mark of any single value is invisible on one of them. This
     is V14's problem — ink over a varying ground — and V14's answer (kerti only)
     does NOT work here, because the ground can be brighter than kerti.
     ══════════════════════════════════════════════════════════════════════════ */
  function breaking(
    k: Kind,
    x: number,
    y: number,
    size: number,
    stage: number,
    uid: string,
  ): string {
    const g = [];
    const t = stage / 3; // 1/3, 2/3, ~1
    const L = 'rgba(255,255,255,0.85)';
    const D = 'rgba(0,0,0,0.80)';
    const pair = (d: string, sw = 1.6): string =>
      `<path d="${d}" fill="none" stroke="${D}" stroke-width="${sw + 1.6}" stroke-linecap="round"/><path d="${d}" fill="none" stroke="${L}" stroke-width="${sw}" stroke-linecap="round"/>`;

    if (k === 'skorur') {
      /* Notches struck in from the edges — wedges, not scratches, so each one has
         a lit face and a shadowed one and reads as material removed. The corners
         bite in as well, which is the part that survives at 15 px. */
      const n = Math.round(t * 7);
      const at = [0.18, 0.62, 0.4, 0.8, 0.28, 0.55, 0.72, 0.35, 0.5, 0.24, 0.66, 0.45];
      for (let i = 0; i < n; i++) {
        const side = i % 4;
        // eslint-disable-next-line security/detect-object-injection -- loop counter against a literal array.
        const f = at[i]!;
        const len = size * (0.14 + t * 0.2);
        const th = size * 0.075;
        let d;
        if (side === 0)
          d = `M${x} ${y + size * f - th} L${x + len} ${y + size * f} L${x} ${y + size * f + th} z`;
        if (side === 1)
          d = `M${x + size} ${y + size * f - th} L${x + size - len} ${y + size * f} L${x + size} ${y + size * f + th} z`;
        if (side === 2)
          d = `M${x + size * f - th} ${y} L${x + size * f} ${y + len} L${x + size * f + th} ${y} z`;
        if (side === 3)
          d = `M${x + size * f - th} ${y + size} L${x + size * f} ${y + size - len} L${x + size * f + th} ${y + size} z`;
        g.push(
          `<path d="${d}" fill="${D}"/><path d="${d}" fill="none" stroke="${L}" stroke-width="1.1"/>`,
        );
      }
      const wd = size * 0.18 * t * 2.6;
      for (const [cx0, cy0, sx, sy] of [
        [x, y, 1, 1],
        [x + size, y + size, -1, -1],
        [x + size, y, -1, 1],
      ] as [number, number, number, number][]) {
        const d = `M${cx0} ${cy0} l${wd * sx} 0 l0 ${wd * sy} z`;
        g.push(
          `<path d="${d}" fill="${D}"/><path d="${d}" fill="none" stroke="${L}" stroke-width="1.2"/>`,
        );
      }
    } else if (k === 'kola') {
      /* The block SINKS. A pressed bevel — dark bottom-right, light top-left —
         growing inward. It changes the block's OUTLINE, which is the only thing
         that still exists at 15 px. */
      const b = 2 + t * 11;
      g.push(
        `<path d="M${x} ${y} h${size} l${-b} ${b} h${-(size - 2 * b)} v${size - 2 * b} l${-b} ${b} z" fill="${L}" opacity="0.30"/>`,
      );
      g.push(
        `<path d="M${x + size} ${y + size} h${-size} l${b} ${-b} h${size - 2 * b} v${-(size - 2 * b)} l${b} ${-b} z" fill="${D}" opacity="0.62"/>`,
      );
      g.push(rect(x + b, y + b, size - 2 * b, size - 2 * b, '#000', { op: 0.1 + t * 0.3 }));
      if (stage === 3)
        g.push(
          rect(x + b, y + b, size - 2 * b, size - 2 * b, 'none', { stroke: L, sw: 1, op: 0.5 }),
        );
    } else if (k === 'studlaberg') {
      /* It cleaves. Straight fractures, the way basalt actually parts — 2 shards,
         then 4, then 6 with the dark interior showing between them. */
      const cuts = [
        [`M${x + size * 0.42} ${y} L${x + size * 0.5} ${y + size}`],
        [
          `M${x} ${y + size * 0.55} L${x + size} ${y + size * 0.44}`,
          `M${x + size * 0.72} ${y + size * 0.44} L${x + size * 0.8} ${y + size}`,
        ],
        [
          `M${x + size * 0.18} ${y} L${x + size * 0.1} ${y + size * 0.55}`,
          `M${x + size * 0.5} ${y + size * 0.5} L${x + size} ${y + size * 0.78}`,
        ],
      ];
      // eslint-disable-next-line security/detect-object-injection -- `stage` is 1-3, `cuts` has 3 entries.
      for (let i = 0; i < stage; i++) for (const d of cuts[i]!) g.push(pair(d, 1.8));
      if (stage === 3) {
        g.push(
          `<path d="M${x + size * 0.42} ${y} L${x + size * 0.5} ${y + size}" stroke="${D}" stroke-width="5" fill="none"/>`,
        );
        g.push(
          `<path d="M${x + size * 0.42} ${y} L${x + size * 0.5} ${y + size}" stroke="${L}" stroke-width="1.4" fill="none"/>`,
        );
      }
    } else {
      /* No crack at all: an instrument reading, drawn on the face. A doubled
         hairline ring filling clockwise, over a scrim that deepens. One big
         circle survives to 15 px where any crack pattern is gone. */
      const r = size * 0.3;
      const cx = x + size / 2,
        cy = y + size / 2;
      g.push(rect(x, y, size, size, '#000', { op: 0.08 + t * 0.34 }));
      g.push(`<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${D}" stroke-width="5"/>`);
      g.push(
        `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${L}" stroke-width="1.2" opacity="0.35"/>`,
      );
      const frac = Math.min(0.98, t);
      const a0 = -Math.PI / 2,
        a1 = a0 + frac * Math.PI * 2;
      const large = frac > 0.5 ? 1 : 0;
      const d = `M${cx} ${cy - r} A${r} ${r} 0 ${large} 1 ${(cx + r * Math.cos(a1)).toFixed(1)} ${(cy + r * Math.sin(a1)).toFixed(1)}`;
      g.push(`<path d="${d}" fill="none" stroke="${D}" stroke-width="6"/>`);
      g.push(`<path d="${d}" fill="none" stroke="${L}" stroke-width="2.6"/>`);
    }
    return `<g clip-path="url(#clip${uid})">${g.join('')}</g>`;
  }

  /* ── The test that matters most: the same cluster over four real grounds.
        🔴 A HUD is never seen on a design sheet's ground. It is seen over black
        lava at midnight and over a glacier at noon, and V14 says a varying
        background voids every ratio computed against the ground. The answer here
        is a PANEL — nótt at 82 %, which over jökull composites to #333B3F and
        still carries kerti at 9.49:1. Without it, no set survives the last band. */
  function groundTest(k: Kind, x: number, y: number, w: number, _uid: string): string {
    const g = [];
    const bands: [string, string][] = [
      ['#0B0D10', 'night'],
      ['#887B6A', 'móberg'],
      ['#69723F', 'birki'],
      ['#DFE7EC', 'jökull'],
    ];
    const bw = w / 4;
    /* The needle gauge is a taller instrument than a bar; the panel grows to
       hold it rather than the gauge being clipped. */
    const h = k === 'vedurmaelir' ? 158 : 96;
    bands.forEach(([col, name], i) => {
      g.push(rect(x + i * bw, y, bw, h, col));
      g.push(txt(x + i * bw + 5, y + h + 14, name, { size: 10.5, fill: C.tolg }));
    });
    g.push(rect(x, y, w, h, C.nott, { op: 0.82 }));
    g.push(bar(k, x + 12, y + 12, w - 24, 62, C.kerti, 12));
    g.push(bar(k, x + 12, y + 38, w - 24, 30, C.lampi, 12));
    g.push(exposure(k, x + 12, y + 64, w - 24, -0.6, 12));
    return g.join('');
  }

  /* ══════════════════════════════════════════════════════════════════════════
     THE INVENTORY — 10 on hand, 24 in the pack (haugur, 2026-08-17).
     ══════════════════════════════════════════════════════════════════════════ */
  function inventory(k: Kind, x: number, y: number, cols: number, rows: number): InventoryPanel {
    const g = [];
    const packW = cols * SLOT + (cols - 1) * GAP;
    const PAD = 24;
    const panelW = BARW + PAD * 2;
    const titleH = 58;
    const packH = rows * SLOT + (rows - 1) * GAP;
    const panelH = titleH + packH + 34 + SLOT + PAD;
    const rx = k === 'kola' ? 8 : 0;

    g.push(rect(x, y, panelW, panelH, C.nott, { rx, op: 0.94 }));
    g.push(rect(x, y, panelW, panelH, 'none', { rx, stroke: C.hairline }));
    g.push(txt(x + PAD, y + 30, 'Pack', { size: 22, font: SERIF, weight: 500, fill: C.kerti }));
    g.push(
      txt(x + panelW - PAD, y + 30, 'E  or  Esc  to close', {
        size: 12,
        fill: C.tolg,
        anchor: 'end',
      }),
    );

    const px = x + PAD + (BARW - packW) / 2;
    const py = y + titleH;
    g.push(txt(px, py - 9, `In the pack — 24  (${cols} × ${rows})`, { size: 11, fill: C.tolg }));
    const fill: Record<number, SlotContent> = {
      0: { item: 'kraekiber', count: 24 },
      1: { item: 'blaber', count: 9 },
      3: { item: 'moberg', count: 41 },
      6: { item: 'birkistofn', count: 7 },
      9: { item: 'moberg', count: 64 },
      12: { item: 'kraekiber', count: 3 },
    };
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        // eslint-disable-next-line security/detect-object-injection -- `i` is derived from the loop bounds.
        g.push(slot(k, px + c * (SLOT + GAP), py + r * (SLOT + GAP), fill[i] ?? {}));
      }

    /* 🔴 The gap and the rule are the whole design. Ten on hand and twenty-four
       in the pack do not share a column count, so nothing lines up — and that is
       turned into the point: the hand row is WIDER than the pack and separated by
       a rule, so it reads as a different place rather than as a fourth row. */
    const ry = py + packH + 20;
    g.push(rect(x + PAD, ry, BARW, 1, C.hairline));
    g.push(txt(x + PAD, ry + 17, 'On hand — 10', { size: 11, fill: C.tolg }));
    const hy = ry + 26;
    const hand: Record<number, SlotContent> = {
      0: { item: 'moberg', count: 12 },
      1: { item: 'birkistofn', count: 3 },
      2: { item: 'kraekiber', count: 18 },
      5: { item: 'blaber', count: 2 },
    };
    for (let i = 0; i < 10; i++)
      // eslint-disable-next-line security/detect-object-injection -- `i` is the loop counter, 0-9.
      g.push(slot(k, x + PAD + i * (SLOT + GAP), hy, { ...(hand[i] ?? {}), selected: i === 2 }));

    return { svg: g.join(''), w: panelW, h: panelH };
  }

  return {
    C,
    SERIF,
    SANS,
    SLOT,
    GAP,
    BARW,
    esc,
    txt,
    eyebrow,
    rect,
    icFlame,
    icAskur,
    icRime,
    icHeat,
    ITEMS,
    picture,
    itKraekiber,
    itBlaber,
    itBirkistofn,
    itMoberg,
    bar,
    exposure,
    lighten,
    darken,
    keyed,
    carvedBar,
    carvedExposure,
    tallyBar,
    tallyExposure,
    haloed,
    struck,
    slot,
    hotbar,
    ghost,
    discardMark,
    packChip,
    breaking,
    groundTest,
    inventory,
  };
}
